import { Button } from "@/components/ui/Button";
import { CodeBlock, ParamTable } from "@/features/docs/components/CodeBlock";
import {
  Callout,
  InlineCode,
  Note,
  P,
  Section,
  Tip,
  docTextLinkClass,
} from "@/features/docs/components/DocPrimitives";
import { docsActiveSectionAtom } from "@/features/docs/docsNavStore";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PAPERCLIP_DOCUMENTED_VERSION } from "@/release";
import { useSetAtom } from "jotai";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

export const DocsPage = () => {
  useDocumentTitle("Docs");
  const setActiveSection = useSetAtom(docsActiveSectionAtom);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const contentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const handleNavigate = useCallback(
    (id: string) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "instant", block: "start" });
      }
      setActiveSection(id);
    },
    [setActiveSection],
  );

  const copyAsMarkdown = useCallback(() => {
    if (!contentRef.current) return;

    const walk = (el: Node): string => {
      if (el.nodeType === Node.TEXT_NODE) return el.textContent || "";
      if (el.nodeType !== Node.ELEMENT_NODE) return "";

      const element = el as HTMLElement;
      const tag = element.tagName.toLowerCase();
      if (getComputedStyle(element).display === "none") return "";
      if (tag === "a" && element.textContent?.trim() === "Built by GXL") return "";

      if (tag === "h1") return "# " + element.textContent?.trim() + "\n\n";
      if (tag === "h2") return "## " + element.textContent?.trim() + "\n\n";
      if (tag === "h3") return "### " + element.textContent?.trim() + "\n\n";

      if (tag === "pre") {
        const code = element.querySelector("code");
        const text = code ? code.textContent : element.textContent;
        return "```\n" + (text?.trim() || "") + "\n```\n\n";
      }

      if (tag === "code" && element.parentElement?.tagName.toLowerCase() !== "pre") {
        return "`" + element.textContent + "`";
      }

      if (tag === "table") {
        const rows = element.querySelectorAll("tr");
        let md = "";
        rows.forEach((row, ri) => {
          const cells = row.querySelectorAll("th, td");
          const vals: string[] = [];
          cells.forEach((c) => vals.push(c.textContent?.trim().replace(/\n/g, " ") || ""));
          md += "| " + vals.join(" | ") + " |\n";
          if (ri === 0) md += "| " + vals.map(() => "---").join(" | ") + " |\n";
        });
        return md + "\n";
      }

      if (tag === "ul") {
        let items = "";
        element.querySelectorAll(":scope > li").forEach((li) => {
          items += "- " + li.textContent?.trim() + "\n";
        });
        return items + "\n";
      }

      if (tag === "strong" || tag === "b") return "**" + element.textContent + "**";
      if (tag === "em" || tag === "i") return "*" + element.textContent + "*";

      if (tag === "p" || element.classList.contains("callout") || element.classList.contains("tip") || element.classList.contains("note")) {
        let inner = "";
        element.childNodes.forEach((c) => { inner += walk(c); });
        return inner.trim() + "\n\n";
      }

      if (tag === "br") return "\n";

      let out = "";
      element.childNodes.forEach((c) => { out += walk(c); });
      return out;
    };

    const lines: string[] = [];
    contentRef.current.childNodes.forEach((c) => {
      const result = walk(c);
      if (result) lines.push(result);
    });

    const md = lines.join("").replace(/\n{3,}/g, "\n\n").trim();
    navigator.clipboard.writeText(md).then(() => {
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );

    const sections = mainRef.current?.querySelectorAll("[id]");
    sections?.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mainRef}
      className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-white"
    >
      <div
        ref={contentRef}
        className="editorial-frame space-y-6"
      >

        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={copyAsMarkdown}
            aria-label={copyState === "copied" ? "Copied to clipboard" : "Copy as Markdown"}
            title={copyState === "copied" ? "Copied to clipboard" : "Copy as Markdown"}
            className={[
              "rounded-none border-0 bg-transparent px-0 text-xs shadow-none hover:bg-transparent",
              copyState === "copied"
                ? "text-zinc-700"
                : "text-zinc-400 hover:text-zinc-800",
            ].join(" ")}
          >
            {copyState === "copied" ? (
              <><Check className="w-3.5 h-3.5" aria-hidden /> <span className="hidden sm:inline">Copied!</span></>
            ) : (
              <><Copy className="w-3.5 h-3.5" aria-hidden /> <span className="hidden sm:inline">Copy as Markdown</span></>
            )}
            <span aria-live="polite" className="sr-only">
              {copyState === "copied" ? "Copied" : ""}
            </span>
          </Button>
        </div>

        {/* HERO */}
        <div className="border-b border-zinc-200/70 pb-8">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900 sm:text-5xl">
              Documentation
            </h1>
            <span className="font-mono text-3xs text-zinc-400">
              Paperclip v{PAPERCLIP_DOCUMENTED_VERSION}
            </span>
          </div>
          <p className="mt-5 max-w-2xl text-base leading-7 text-zinc-500">
            Search, read, and analyze 11M+ papers, 225K+ regulatory documents,
            1M+ clinical trials, 150M+ abstracts, and biological databases
            through the CLI, Python SDK, or MCP.
          </p>
        </div>

        {/* OVERVIEW */}
        <Section id="overview" title="Overview">
          <P>
            Paperclip exposes the same corpus through a <strong>command-line interface</strong>, an
            optional <strong>Python SDK</strong> (<InlineCode>gxl_paperclip</InlineCode>), and{" "}
            <strong>MCP</strong>, allowing for the best tool to fit the task. Terminals and shell pipelines
            use the CLI; scripts, notebooks, and services often use the SDK; coding agents can use
            either, depending on environment. Every paper is a directory with full text, sections,
            figures, and supplements on a virtual filesystem at <InlineCode>/papers/</InlineCode>.
          </P>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-disc">
            <li>Search with natural language or regex across 11M+ papers, 225K+ FDA documents, 1M+ clinical trials, 170M+ patent publications, 150M+ abstracts, and biological databases</li>
            <li>Run subagents to read papers in parallel and return answers to queries</li>
            <li>Pipe results through standard Unix tools (<InlineCode>grep</InlineCode>, <InlineCode>awk</InlineCode>, <InlineCode>sed</InlineCode>, <InlineCode>jq</InlineCode>, etc.)</li>
            <li>Ask questions over figures</li>
            <li>Query the database directly with SQL</li>
          </ul>
        </Section>

        {/* INSTALLATION */}
        <Section id="install" title="Installation">
          <P>
            There are two ways to connect: <strong>install Paperclip on your machine</strong>{" "}
            (the full command set — use it yourself in a terminal, or let your coding agent
            drive it), or point your agent at the hosted <strong>MCP server</strong> (no
            local install). Commands below are labeled with where to run them —{" "}
            <InlineCode>your terminal</InlineCode> means a regular shell, not your agent's
            chat window.
          </P>

          {/* Method 1: curl */}
          <div className="mb-6">
            <h3 className="mb-2 flex items-baseline gap-3 text-base font-medium text-zinc-900">
              One-line installer
              <span className="font-mono text-3xs uppercase tracking-[0.14em] text-zinc-400">recommended</span>
            </h3>
            <CodeBlock code={`curl -fsSL https://paperclip.gxl.ai/install.sh | bash`} language="bash" title="your terminal" />
            <div className="text-sm text-zinc-500 -mt-2 mb-1">
              Handles everything: installs to <InlineCode>~/.paperclip/</InlineCode>, signs you in, and sets up your coding agent.
            </div>
            <Note>
              Run this in your own terminal, not via your coding agent — the sign-in
              step opens a browser and waits for you.
            </Note>
          </div>

          {/* Verify */}
          <div className="mt-4 border-t border-zinc-200/70 pt-4">
            <h3 className="mb-2 text-base font-medium text-zinc-900">Verify your setup</h3>
            <CodeBlock
              code={`paperclip config\n# Server:  https://paperclip.gxl.ai\n# Auth:    ✓ you@example.com\n# Config:  ~/.paperclip`}
              language="bash"
              title="your terminal"
            />
          </div>

          {/* MCP */}
          <div className="mt-6 border-t border-zinc-200/70 pt-5">
            <h3 className="mb-3 text-base font-medium text-zinc-900">Alternative: MCP server</h3>
            <P>
              Use Paperclip as a hosted MCP server instead — no local install needed.
              Point your client at:
            </P>
            <CodeBlock code={`https://paperclip.gxl.ai/mcp`} language="text" title="MCP server URL" />
            <Note>
              Claude’s tools are the Paperclip commands, one tool per command. Setup for
              Claude Desktop, claude.ai, and Cowork is in the{" "}
              <Link to="/install#install-claude-desktop" className={docTextLinkClass}>
                Claude install section
              </Link>
              .
            </Note>
            <P>
              Step-by-step guides for Claude Code, Claude Desktop, Codex, ChatGPT, Cursor,
              Windsurf, Antigravity, and Windows are on the{" "}
              <Link to="/install" className={docTextLinkClass}>install page</Link>.
            </P>
          </div>
        </Section>

        {/* QUICK START */}
        <Section id="quickstart" title="Quick start">
          <CodeBlock
            code={`# Search for papers
paperclip search -s papers "CRISPR base editing efficiency"

# Read a paper's metadata
paperclip cat /papers/bio_4f78753a6feb/meta.json

# Preview the first 50 lines
paperclip head -50 /papers/bio_4f78753a6feb/content.lines

# Grep within a single paper
paperclip grep -i "binding affinity" /papers/bio_4f78753a6feb/content.lines

# Grep across the entire corpus
paperclip grep -i "alphamissense" /papers/

# Map over search results with an AI reader
paperclip map --from s_abc123 "What methods were used?"

# Save results to a local file
paperclip search -s papers "CRISPR" -n 5 > results.txt`}
            language="bash"
          />
          <P>
            Any command works directly as a subcommand. Use{" "}
            <InlineCode>paperclip bash {"'"}...{"'"}</InlineCode> for pipes and chains:
          </P>
          <CodeBlock
            code={`paperclip bash 'search -s papers "protein folding" | grep "deep learning"'`}
            language="bash"
          />
        </Section>

        {/* USING WITH AGENTS */}
        <Section id="claude-code" title="Using with agents">
          <P>
            Once connected (local install or MCP), invoke Paperclip from Claude Code
            by mentioning <InlineCode>/paperclip</InlineCode> in your prompt. Claude
            loads the skill automatically and runs commands on your behalf.
          </P>
          <CodeBlock
            code={`❯ Using /paperclip, find the relative frequency of methods which use Boltz
vs. Chai models in papers

● Skill(paperclip)
  ⎿  Successfully loaded skill

● I'll search for papers using both Boltz and Chai as structure prediction
  models, then dig into the methods sections.

● Bash(paperclip search -s papers "Boltz structure prediction" -n 50 --all 2>&1 | head -60)
  ⎿  Found 29 papers  [s_1cbc742e]

● Bash(paperclip search -s papers "Chai-1 structure prediction" -n 50 --all 2>&1 | head -60)
  ⎿  Found 20 papers  [s_0ef54807]`}
            language="text"
          />
          <P>
            You can also run Paperclip commands directly from the terminal alongside Claude:
          </P>
          <CodeBlock
            code={`# In your terminal (while Claude Code is running)
paperclip search -s papers "GLP-1 receptor agonist efficacy" -n 5
paperclip map --from s_abc123 "What were the primary endpoints?"
paperclip grep -i "hazard ratio" /papers/med_84637b0c77f5/content.lines`}
            language="bash"
          />
          <Tip>
            Claude Code will automatically use <InlineCode>search → map → synthesize</InlineCode>{" "}
            workflows when you ask research questions. The skill teaches it the full command set.
          </Tip>
        </Section>

        {/* DATA SOURCES */}
        <Section id="data-sources" title="Data sources">
          <P>
            Paperclip searches and reads across thirteen sources: the full{" "}
            <a href="https://www.biorxiv.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              bioRxiv
            </a>{" "}
            and{" "}
            <a href="https://www.medrxiv.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              medRxiv
            </a>{" "}
            preprint archives,{" "}
            <a href="https://www.ncbi.nlm.nih.gov/pmc/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              PubMed Central
            </a>{" "}
            (PMC) for open-access peer-reviewed literature,{" "}
            <a href="https://arxiv.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              arXiv
            </a>{" "}
            for CS/ML/physics/math preprints,{" "}
            abstracts from{" "}
            <a href="https://pubmed.ncbi.nlm.nih.gov/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              PubMed
            </a>{" "}
            and{" "}
            <a href="https://www.crossref.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              Crossref
            </a>{" "}
            for broad scientific coverage,{" "}
            <a href="https://www.fda.gov/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              FDA
            </a>{" "}
            regulatory documents (including PMDA and EMA),{" "}
            <a href="https://clinicaltrials.gov/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              ClinicalTrials.gov
            </a>
            , international clinical trial registries, a global catalog of 170 million patent
            publications from 75 jurisdictions,{" "}
            <a href="https://www.sec.gov/edgar" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              SEC EDGAR
            </a>{" "}
            company filings, and biological databases &mdash;{" "}
            <a href="https://www.uniprot.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              UniProt
            </a>
            ,{" "}
            <a href="https://www.rcsb.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              PDB
            </a>
            , and{" "}
            <a href="https://www.ebi.ac.uk/chembl/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
              ChEMBL
            </a>
            . Full-text sources support{" "}
            <InlineCode>search</InlineCode>, <InlineCode>grep</InlineCode>, <InlineCode>cat</InlineCode>, and <InlineCode>sql</InlineCode>.
            Abstract-only sources support <InlineCode>search</InlineCode> and <InlineCode>sql</InlineCode>.
            The patent corpus includes full-text claims and descriptions for 1.8 million
            drug-related patents and 30 million compound annotations.
            Protein databases support <InlineCode>sql</InlineCode>, <InlineCode>cat</InlineCode>, <InlineCode>grep</InlineCode>, and <InlineCode>ls</InlineCode> via <InlineCode>/proteins/</InlineCode>.
          </P>
          <div className="my-5 overflow-x-auto border-t border-zinc-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-4 py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">Source</th>
                  <th className="px-4 py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">Type</th>
                  <th className="px-4 py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400 text-right whitespace-nowrap"># Documents</th>
                  <th className="px-4 py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">Years</th>
                  <th className="px-4 py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">Update frequency</th>
                  <th className="px-4 py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200/70 border-t border-zinc-200/70">
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.biorxiv.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      bioRxiv
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~434K</td>
                  <td className="px-4 py-2.5 text-zinc-600">2014&ndash;present</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every 5 minutes</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Preprint server for the biological sciences, operated by Cold Spring Harbor Laboratory. Full text arrives about 2 days after posting; title and abstract at once.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.medrxiv.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      medRxiv
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~95K</td>
                  <td className="px-4 py-2.5 text-zinc-600">2019&ndash;present</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every 5 minutes</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Preprint server for the health and clinical sciences. Full text arrives about 2 days after posting; title and abstract at once.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.ncbi.nlm.nih.gov/pmc/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      PubMed Central (PMC)
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~8.3M</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every minute</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Open-access papers only. Includes top journals such as <em>Nature</em>, <em>Science</em>, <em>Cell</em>, <em>NEJM</em>, <em>The Lancet</em>, and more.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://arxiv.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      arXiv
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~3.2M</td>
                  <td className="px-4 py-2.5 text-zinc-600">1991&ndash;present</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Each daily announcement</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    All arXiv categories. arXiv announces new papers once a day, Sunday to Thursday. Use <InlineCode>-s arxiv</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://pubmed.ncbi.nlm.nih.gov/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      Abstracts (PubMed + Crossref)
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Abstracts only</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~56M</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every minute</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Title + abstract search only (no full text), from PubMed and Crossref. Use <InlineCode>-s abstracts_only</InlineCode>. Useful for broad literature surveys.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.fda.gov/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      FDA (US)
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~223K</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every 15&ndash;60 minutes</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Drug approval reviews, labeling, CBER, advisory committee meetings. Use <InlineCode>-s fda</InlineCode>. Access via <InlineCode>/fda/us/</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://clinicaltrials.gov/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      ClinicalTrials.gov
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~581K</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every minute</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    US clinical trial registry. ClinicalTrials.gov publishes once a day; new data arrives within a minute. Use <InlineCode>-s trials/us</InlineCode>. Access via <InlineCode>/clinicaltrials/us/</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    Intl Regulatory + Trials
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~478K</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Every minute &ndash; weekly</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Japan PMDA (<InlineCode>/fda/jp/</InlineCode>), EU EPAR (<InlineCode>/fda/eu/</InlineCode>), ChiCTR, UMIN, JRCT, EudraCT, CTIS, ISRCTN, WHO ICTRP (13 registries). CTIS and ISRCTN every minute, UMIN every 15 minutes, EU CTR, EPAR and PMDA hourly, other registries weekly through WHO ICTRP. Use <InlineCode>-s fda/jp</InlineCode>, <InlineCode>-s trials/intl</InlineCode>, etc.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    Patents
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Metadata + full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~170M</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Weekly</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Global metadata from 75 jurisdictions, with claims and descriptions for 1.8M drug-related patents and 30M compound annotations. Use <InlineCode>-s patents</InlineCode>. Access via <InlineCode>/patents/</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.sec.gov/edgar" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      SEC EDGAR
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Full text</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~3.3M</td>
                  <td className="px-4 py-2.5 text-zinc-600">1993&ndash;present</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Daily (weekdays)</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Company filings: 10-K, 10-Q, 8-K, proxies, and their exhibits. Use <InlineCode>-s sec</InlineCode>. Access via <InlineCode>/sec/</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.uniprot.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      UniProt
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Structured</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~575K</td>
                  <td className="px-4 py-2.5 text-zinc-600">Swiss-Prot</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Each release</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Full Swiss-Prot: sequences, function, domains, PTMs, variants, GO, disease associations. Use <InlineCode>-s uniprot</InlineCode>. Access via <InlineCode>/proteins/</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.rcsb.org/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      RCSB PDB
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Structured</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~256K</td>
                  <td className="px-4 py-2.5 text-zinc-600">All years</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Periodic</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Experimental structure metadata: entries, polymer/nonpolymer entities, assemblies, ligands, DrugBank. Use <InlineCode>-s pdb</InlineCode>. Access via <InlineCode>/proteins/</InlineCode>.
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2.5">
                    <a href="https://www.ebi.ac.uk/chembl/" target="_blank" rel="noopener noreferrer" className={docTextLinkClass}>
                      ChEMBL
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-600">Structured</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-right">~2.9M</td>
                  <td className="px-4 py-2.5 text-zinc-600">v37</td>
                  <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">Periodic</td>
                  <td className="px-4 py-2.5 text-zinc-500 max-w-[200px]">
                    Bioactivities, compounds, drug annotations, mechanism of action, assays, target mappings. Use <InlineCode>-s chembl</InlineCode>. Access via <InlineCode>/proteins/</InlineCode>.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <P>
            Regulatory documents, clinical trials, and patents are accessed via virtual directory paths (<InlineCode>/fda/</InlineCode>, <InlineCode>/clinicaltrials/</InlineCode>, and <InlineCode>/patents/</InlineCode>).
            Biological databases (UniProt, PDB, ChEMBL) are unified under <InlineCode>/proteins/</InlineCode> and support cross-database SQL joins.
            If you have suggestions for what we should index next, reach out to{" "}
            <a href="mailto:support@gxl.ai" className={docTextLinkClass}>
              support@gxl.ai
            </a>
            .
          </P>
        </Section>

        {/* SEARCH & DISCOVERY */}

        <p className="text-sm text-zinc-500 mt-8 mb-6">
          Search and discovery commands use dedicated backends &mdash; search indexes, parallel
          document processing, vision models, and SQL engines. They look and feel like regular
          shell commands, but each one is powered by specialized infrastructure.
        </p>

        {/* search */}
        <Section id="search" title="paperclip search">
          <Callout>
            Hybrid search (BM25 + vector similarity, fused) over one corpus.{" "}
            <InlineCode>-s</InlineCode> is required. Use <InlineCode>-s papers</InlineCode> for
            all full-text papers (PMC, bioRxiv, medRxiv, and arXiv), or name one corpus such as{" "}
            <InlineCode>-s pmc</InlineCode>. A path such as <InlineCode>/fda/us</InlineCode> also selects the corpus. For
            claim-shaped questions use <InlineCode>paperclip evidence</InlineCode>.
          </Callout>
          <CodeBlock
            code={`paperclip search -s papers "protein structure prediction" -n 20
paperclip search -s papers -e "genome-wide association study"
paperclip search -s papers "cancer" --year 2024 --sort date
paperclip search -s papers "mechanisms of immune evasion in solid tumors"
paperclip search -s papers -e "TP53 R175H"
paperclip search -s pmc --since 1y "gene therapy"
paperclip search --source biorxiv,pmc "gene therapy"
paperclip search -s papers -a "David Baker" -n 20
paperclip search -s papers -r "CRISPR.*Cas9"
paperclip search -s papers -c "CRISPR"
paperclip search -s fda "pembrolizumab"
paperclip search -s trials/us "phase 3 NSCLC"
paperclip search -s patents "covalent KRAS inhibitor"
paperclip search "semaglutide" /fda/us
paperclip search "breast cancer" /clinicaltrials/us`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "QUERY", type: "string", required: true, description: "Natural language search query (or regex with -r)" },
              { name: "-n", type: "int", description: "Number of results (max: 1000)", default: "100" },
              { name: "-s, --source", type: "string", required: true, description: "Corpus to search. Required. papers (all full-text papers), pmc, biorxiv, medrxiv, arxiv, abstracts, fda, fda/jp, fda/eu, trials, trials/us, trials/cn, trials/jp, trials/eu, patents, proteins, geo, clipboard. Comma-separated for more than one. A path such as /fda/us also selects the corpus." },
              { name: "-e, --exact", type: "flag", description: "Exact phrase matching (sets mode to phrase)" },
              { name: "-r, --regex", type: "flag", description: "Treat query as a regex pattern (uses trigram grep internally)" },
              { name: "-a, --author", type: "string", description: "Search by author name" },
              { name: "-t, --title", type: "string", description: "Search by title" },
              { name: "-c, --count", type: "flag", description: "Count mode — return only the match count, no results" },
              { name: "-M, --metadata", type: "flag", description: "Search metadata only (not full text)" },
              { name: "--sort", type: "string", description: "Sort order: relevance or date", default: "relevance" },
              { name: "--since", type: "string", description: "Time filter: 30d, 7d, 6m, 1y, etc." },
              { name: "--recent", type: "flag", description: "Shorthand for last year" },
              { name: "--year", type: "string", description: "Filter by publication year (e.g. 2024)" },
              { name: "--category", type: "string", description: "Filter by subject category (e.g. Neuroscience)" },
              { name: "--journal", type: "string", description: "Filter by journal name" },
              { name: "--article-type", type: "string", description: "Filter by article type (e.g. review-article, research-article). PMC only." },
              { name: "-T, --type", type: "string", description: "Filter by document type (e.g. youtube, meeting)" },
              { name: "--tag", type: "string", description: "Tag for result accumulation — multiple searches with the same tag merge results" },
              { name: "--all", type: "flag", description: "Search full corpus (slower, removes index optimizations)" },
              { name: "--quiet", type: "flag", description: "Minimal output" },
              { name: "--json", type: "flag", description: "Return results as JSON" },
            ]}
          />
          <P>
            Every search returns a <strong>results ID</strong> (e.g. <InlineCode>s_1907a2d0</InlineCode>)
            that can be passed to <InlineCode>map</InlineCode>, <InlineCode>filter</InlineCode>,{" "}
            or <InlineCode>grep --from</InlineCode>.
          </P>
        </Section>

        {/* searches */}
        <Section id="searches" title="paperclip searches">
          <Callout>Run multiple search queries in parallel and merge results.</Callout>
          <CodeBlock
            code={`# Run three searches in parallel
paperclip searches "CRISPR delivery" "gene editing cancer" "viral vectors"

# Tag results for accumulation across calls
paperclip searches --quiet --tag crispr "CRISPR delivery" "gene editing cancer"

# All results accumulated into: s_abc123  [tag: crispr]  (58 unique papers)`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "QUERIES", type: "string[]", required: true, description: "One or more search queries (quoted strings)" },
              { name: "-n", type: "int", description: "Limit per search (max: 1000)", default: "50" },
              { name: "-m", type: "string", description: "Match mode: any, all, 50%, 75%, phrase" },
              { name: "-e, --exact", type: "flag", description: "Exact phrase matching" },
              { name: "--recent", type: "flag", description: "Limit to recent papers" },
              { name: "--quiet", type: "flag", description: "Minimal output — only the accumulated count and result ID" },
              { name: "--tag", type: "string", description: "Tag for result accumulation" },
            ]}
          />
        </Section>

        {/* lookup */}
        <Section id="lookup" title="paperclip lookup">
          <Callout>Look up papers by a specific metadata field.</Callout>
          <CodeBlock
            code={`paperclip lookup doi 10.1101/2024.01.15.575556
paperclip lookup author "David Baker" -n 10
paperclip lookup title "CRISPR base editing"
paperclip lookup pmc PMC7194329
paperclip lookup pmid 32943797
paperclip lookup arxiv 2403.03507
paperclip lookup journal "Nature Medicine"
paperclip lookup keywords "CRISPR"
paperclip lookup year 2024
paperclip lookup type review-article`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FIELD", type: "string", required: true, description: "Field to search (see below)" },
              { name: "VALUE", type: "string", required: true, description: "Value to look up" },
              { name: "-n", type: "int", description: "Max results", default: "25" },
            ]}
          />
          <h3 className="text-base font-medium text-zinc-900 mt-6 mb-3">Available fields</h3>
          <ParamTable
            params={[
              { name: "doi", type: "TEXT", description: "Digital Object Identifier" },
              { name: "title", type: "TEXT", description: "Paper title" },
              { name: "author", type: "TEXT", description: "Author name" },
              { name: "abstract", type: "TEXT", description: "Abstract text" },
              { name: "source", type: "TEXT", description: "biorxiv, medrxiv, pmc, arxiv, openalex, fda, or trials" },
              { name: "month_year, date", type: "TEXT", description: "Publication date (bioRxiv/medRxiv)" },
              { name: "pmc", type: "TEXT", description: "PMC ID (e.g. PMC7194329). PMC only." },
              { name: "pmid", type: "TEXT", description: "PubMed ID. PMC only." },
              { name: "journal", type: "TEXT", description: "Journal name. PMC only." },
              { name: "publisher", type: "TEXT", description: "Publisher name. PMC only." },
              { name: "type", type: "TEXT", description: "Article type (e.g. review-article). PMC only." },
              { name: "keywords", type: "TEXT", description: "Keywords. PMC only." },
              { name: "category", type: "TEXT", description: "Subject categories. PMC only." },
              { name: "license", type: "TEXT", description: "License type. PMC only." },
              { name: "year", type: "TEXT", description: "Publication year. PMC only." },
              { name: "volume, issue, issn", type: "TEXT", description: "Journal volume, issue, ISSN. PMC only." },
            ]}
          />
        </Section>

        {/* grep */}
        <Section id="grep" title="paperclip grep">
          <Callout>
            Regex search within a paper or across the entire corpus. Corpus-wide grep uses a
            trigram index &mdash; sub-second across 11M+ documents.
          </Callout>
          <CodeBlock
            code={`# Corpus-wide regex search
paperclip grep -i "crispr\\|cas9" /papers/

# Within a single paper
paperclip grep -i "binding" /papers/bio_4f78753a6feb/content.lines

# Show context lines
paperclip grep -i -C 2 "p53 mutation" /papers/bio_4f78753a6feb/content.lines

# Within a search result set
paperclip grep --from s_abc123 "kinase"

# Multiple patterns (OR'd together)
paperclip grep -e "CRISPR" -e "Cas9" /papers/bio_4f78753a6feb/content.lines`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "PATTERN", type: "string", required: true, description: "Regex pattern (or use -e for multiple)" },
              { name: "PATH", type: "string", description: "/papers/ for corpus-wide, or /papers/<id>/file.lines for single paper" },
              { name: "-i", type: "flag", description: "Case-insensitive matching" },
              { name: "-n", type: "flag", description: "Show line numbers" },
              { name: "-c", type: "flag", description: "Count corpus documents exactly for up to 500 bitmap candidates, then approximate; count matching lines for files" },
              { name: "-v", type: "flag", description: "Invert match (show non-matching lines)" },
              { name: "-o", type: "flag", description: "Print only the matching part of lines" },
              { name: "-w", type: "flag", description: "Match whole words only" },
              { name: "-l", type: "flag", description: "List only filenames with matches" },
              { name: "-h", type: "flag", description: "Suppress filename prefix" },
              { name: "-F", type: "flag", description: "Fixed strings (literal match, no regex)" },
              { name: "-e PATTERN", type: "string", description: "Explicit pattern (repeatable for multi-pattern OR)" },
              { name: "-m NUM", type: "int", description: "Stop after NUM matches" },
              { name: "-A NUM", type: "int", description: "Show NUM lines after each match" },
              { name: "-B NUM", type: "int", description: "Show NUM lines before each match" },
              { name: "-C NUM", type: "int", description: "Show NUM lines of context (before and after)" },
              { name: "--from", type: "string", description: "Grep within a search result set (e.g. --from s_abc123)" },
            ]}
          />
        </Section>

        {/* scan */}
        <Section id="scan" title="paperclip scan">
          <Callout>
            Multi-pattern grep &mdash; search for several keywords in a single pass, results grouped by pattern.
          </Callout>
          <CodeBlock
            code={`paperclip scan /papers/bio_4f78753a6feb/content.lines "AAV" "efficiency" "in vivo"
paperclip scan -i -C 3 /papers/bio_4f78753a6feb/content.lines "p53" "MDM2"`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FILE", type: "string", required: true, description: "Path to the file to scan" },
              { name: "PATTERNS", type: "string[]", required: true, description: "One or more patterns (quoted strings)" },
              { name: "-i", type: "flag", description: "Case-insensitive matching" },
              { name: "-C NUM", type: "int", description: "Lines of context per match", default: "5" },
            ]}
          />
          <Tip>
            <InlineCode>scan</InlineCode> is faster than running multiple <InlineCode>grep</InlineCode>{" "}
            calls &mdash; it reads the file once and matches all patterns in a single pass.
          </Tip>
        </Section>

        {/* sql */}
        <Section id="sql" title="paperclip sql">
          <Callout>Run read-only SQL queries against the unified papers database.</Callout>
          <CodeBlock
            code={`# Count papers by source
paperclip sql "SELECT source, COUNT(*) FROM documents GROUP BY source"

# Find papers by author
paperclip sql "SELECT title, doi, source FROM documents WHERE authors ILIKE '%Doudna%' LIMIT 5"

# Top journals (PMC papers)
paperclip sql "SELECT journal_title, COUNT(*) c FROM documents WHERE source = 'pmc' GROUP BY 1 ORDER BY c DESC LIMIT 10"

# Recent papers about a topic
paperclip sql "SELECT title, doi, pub_date FROM documents WHERE abstract_text ILIKE '%CRISPR%' ORDER BY pub_date DESC LIMIT 10"`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "QUERY", type: "string", required: true, description: "SQL SELECT statement" },
              { name: "--source, -s", type: "string", description: "Filter by source: biorxiv, medrxiv, pmc, arxiv, abstracts_only, fda, trials, patents, proteins, uniprot, pdb, chembl", default: "all" },
            ]}
          />
          <P>
            Only <InlineCode>SELECT</InlineCode> on the <InlineCode>documents</InlineCode> table
            is allowed. 15-second timeout, 200-row limit.
          </P>
          <h3 className="font-mono text-sm font-medium mt-8 mb-3">
            Schema: <InlineCode>documents</InlineCode>
          </h3>
          <ParamTable
            params={[
              { name: "id", type: "TEXT", description: "Paper identifier" },
              { name: "title", type: "TEXT", description: "Paper title" },
              { name: "doi", type: "TEXT", description: "Digital Object Identifier" },
              { name: "authors", type: "TEXT", description: "Comma-separated author list" },
              { name: "source", type: "TEXT", description: "'biorxiv', 'medrxiv', 'pmc', 'arxiv', 'openalex', 'fda', or 'trials'" },
              { name: "abstract_text", type: "TEXT", description: "Paper abstract" },
              { name: "pub_date", type: "TEXT", description: "Publication date (text)" },
              { name: "journal_title", type: "TEXT", description: "Journal name. PMC only." },
              { name: "article_type", type: "TEXT", description: "e.g. research-article, review-article. PMC only." },
              { name: "pmid", type: "TEXT", description: "PubMed ID. PMC only." },
              { name: "keywords", type: "JSONB", description: "Array of keywords. PMC only." },
              { name: "categories", type: "JSONB", description: "Array of subject categories. PMC only." },
              { name: "pub_year", type: "INT", description: "Publication year. PMC only." },
              { name: "created_at", type: "TIMESTAMP", description: "When the record was indexed" },
            ]}
          />
          <Note>
            PMC-only columns are NULL for bioRxiv/medRxiv papers. Use{" "}
            <InlineCode>WHERE source = {"'"}pmc{"'"}</InlineCode> when querying these fields.
          </Note>
        </Section>

        {/* export */}
        <Section id="export" title="paperclip export">
          <Callout>Export SQL query results to CSV and a table artifact (up to 1,000 rows).</Callout>
          <CodeBlock
            code={`paperclip export "SELECT title, doi, authors FROM documents WHERE source = 'biorxiv' ORDER BY created_at DESC LIMIT 100"

paperclip export --desc "CRISPR papers 2024" "SELECT DISTINCT d.title, d.doi FROM documents d JOIN content_blocks cb ON d.document_id = cb.document_id WHERE cb.content ILIKE '%CRISPR%' AND d.month_year >= '2024-01'"`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "QUERY", type: "string", required: true, description: "SQL SELECT or WITH statement" },
              { name: "--desc", type: "string", description: "Description for the export" },
            ]}
          />
          <P>
            The <InlineCode>export</InlineCode> command has access to additional tables
            beyond <InlineCode>documents</InlineCode>: <InlineCode>content_blocks</InlineCode>{" "}
            (id, document_id, line_number, content, section, block_type) and{" "}
            <InlineCode>figures</InlineCode> (document_id, graphic, source_path). Up to 1,000 rows.
          </P>
        </Section>

        {/* ANALYSIS */}

        {/* map */}
        <Section id="map" title="paperclip map">
          <Callout>
            Run parallel AI reader tasks over multiple papers from a search result.
          </Callout>
          <P>
            Each paper gets read in full by an LLM that extracts the information you ask for.
            Results are returned with per-paper answers.
          </P>
          <P>
            Typical workflow: <InlineCode>search</InlineCode> →{" "}
            <InlineCode>map</InlineCode> → synthesize from results.
          </P>
          <CodeBlock
            code={`# Step 1: search for papers
$ paperclip search -s papers "AAV gene therapy delivery" -n 3

Found 3 papers  [s_1907a2d0]

  1. Covalently linked adenovirus-AAV complexes as a novel
     platform technology for gene therapy
     Logan Thrasher Collins, Wandy Beatty, et al.
     bio_f402e4cf6e4a · bioRxiv · 2024-08-21

  2. Myocardial infarction creates a critical time window
     for AAV gene therapy
     Gonglie Chen, Yueyang Zhang, et al.
     bio_f2997a136fe7 · bioRxiv · 2024-06-10

  3. A facile chemical strategy to synthesize precise
     AAV-protein conjugates for targeted gene delivery
     Quan Pham, Jake Glicksman, et al.
     bio_ea44a956784e · bioRxiv · 2024-07-20

[197ms, saved to s_1907a2d0]`}
            language="bash"
            title="Step 1 — Search"
          />
          <CodeBlock
            code={`# Step 2: map over those results
$ paperclip map --from s_1907a2d0 \\
    "What delivery vector was used and what transduction efficiency was reported?"

Map complete: 3/3 papers

  ✓ Covalently linked adenovirus-AAV complexes as a novel platform...
    The study used covalently linked adenovirus-AAV (Ad-AAV) complexes.
    In vitro, approximately 80% transduction in HEK293 cells.

  ✓ Myocardial infarction creates a critical time window for AAV...
    The study utilized AAV9. Transduction efficiency: 32.4 ± 4.5% at
    1 day post-MI vs 16.2 ± 3.1% in sham controls.

  ✓ A facile chemical strategy to synthesize precise AAV-protein...
    AAV2-HER2 conjugates for targeted delivery to HER2+ cancer cells.
    Explicit transduction efficiency percentages were not provided.`}
            language="bash"
            title="Step 2 — Map"
          />
          <ParamTable
            params={[
              { name: "QUERY", type: "string", required: true, description: "Question applied to every paper" },
              { name: "--from", type: "string", description: "Results ID from a previous search (s_xxx). If omitted, uses the last search result." },
              { name: "--document-ids", type: "string", description: "Comma-separated document IDs to map over directly, instead of --from." },
              { name: "--output-schema", type: "string", description: "Draft 2020-12 JSON Schema for each paper's complete output. --output_schema remains an alias." },
              { name: "--filter", type: "string", description: "Regex pre-filter applied to each paper before the reader runs (alias: --grep)." },
              { name: "--limit", type: "int", description: "Max number of papers to process" },
              { name: "--offset", type: "int", description: "Skip first N papers in the result set" },
            ]}
          />
          <P>
            Every map returns a <strong>map ID</strong> (e.g. <InlineCode>m_def456</InlineCode>)
            that can be passed to <InlineCode>reduce</InlineCode> to synthesize the per-paper
            answers into a single narrative or table.
          </P>
          <P>
            <InlineCode>--output-schema</InlineCode> works with the default quick reader.
            It requires one strict JSON value from each paper. Paperclip validates that value
            against the schema. Invalid output gets one correction attempt. If the corrected
            output is still invalid, that paper fails.
          </P>
          <CodeBlock
            code={`paperclip map --from s_1907a2d0 \\
  --output-schema '{"type":"object","required":["effect"],"additionalProperties":false,"properties":{"effect":{"type":["number","null"]}}}' \\
  "Extract the reported effect. Use null when it is not reported."`}
            language="bash"
            title="Strict structured output"
          />
          <Tip>
            Use <InlineCode>required</InlineCode> for mandatory fields. Use{" "}
            <InlineCode>additionalProperties: false</InlineCode> to reject extra fields.
            Allow <InlineCode>null</InlineCode> when a value can be unavailable. Paperclip
            does not add citation fields unless your schema defines them.
          </Tip>
          <Tip>
            Keep search results to 3–10 papers for fast map execution. Use{" "}
            <InlineCode>-n 5</InlineCode> on your search to limit the set. The map command shows
            a live progress bar during execution.
          </Tip>
        </Section>

        {/* reduce */}
        <Section id="reduce" title="paperclip reduce">
          <Callout>
            Synthesize the per-paper answers from a <InlineCode>map</InlineCode> into a single
            cohesive narrative or structured table.
          </Callout>
          <P>
            Run <InlineCode>reduce</InlineCode> after a <InlineCode>map</InlineCode>, passing the
            map ID with <InlineCode>--from</InlineCode>. If <InlineCode>--from</InlineCode> is
            omitted, the most recent map result is used.
          </P>
          <CodeBlock
            code={`# Synthesize a map into prose
paperclip reduce --from m_def456 "Compare the delivery vectors and efficiencies"

# Produce a structured comparison table
paperclip reduce --from m_def456 --strategy table \\
    --columns "vector,cell type,efficiency" "Compare delivery across papers"

# Omit --from to reduce the most recent map
paperclip reduce "Summarize the consensus across these papers"`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "QUERY", type: "string", description: "What to synthesize across the mapped papers." },
              { name: "--from", type: "string", description: "Map results ID (m_xxx) from a previous map. Defaults to the most recent map result." },
              { name: "--strategy", type: "string", description: "Reduce strategy: summarize, table, themes, consensus, bullet_points, extract.", default: "summarize" },
              { name: "--columns", type: "string", description: "Comma-separated column names — used with --strategy table." },
            ]}
          />
          <P>
            Reduce returns an artifact ID alongside the synthesized output, so the result can be
            referenced later with <InlineCode>results</InlineCode>.
          </P>
        </Section>

        {/* filter */}
        <Section id="filter" title="paperclip filter">
          <Callout>
            Filter search results for relevance to a specific query, removing off-topic papers.
          </Callout>
          <CodeBlock
            code={`# Filter results — removes irrelevant papers
paperclip filter --from s_abc123 "AAV delivery to the lung"

# Require at least N papers to pass (exit code 1 if fewer survive)
paperclip filter --from s_abc123 --require 5 "AAV delivery to the lung"`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "--from", type: "string", required: true, description: "Results ID to filter (s_xxx)" },
              { name: "QUERY", type: "string", required: true, description: "The user's original query (used to judge relevance)" },
              { name: "--require N", type: "int", description: "Fail (exit code 1) if fewer than N papers survive. Filtered results are still saved." },
            ]}
          />
          <P>
            Previously-evaluated papers are cached &mdash; re-running filter after adding new
            search results only evaluates new papers.
          </P>
        </Section>

        {/* ask-image */}
        <Section id="ask-image" title="paperclip ask-image">
          <Callout>
            Analyze figures from papers using vision AI. Requires a paper directory context.
          </Callout>
          <CodeBlock
            code={`# List available figures for a paper
paperclip bash 'cd /papers/bio_4f78753a6feb/ && ask_image --list'

# Describe a figure (default question)
paperclip bash 'cd /papers/bio_4f78753a6feb/ && ask_image fig1.tif'

# Ask a specific question about a figure
paperclip bash 'cd /papers/bio_4f78753a6feb/ && ask_image fig1.tif "What does this figure show?"'

# Analyze multiple figures
paperclip bash 'cd /papers/bio_4f78753a6feb/ && ask_image fig1.tif fig2.tif "Compare these"'

# First, list figures to see what's available
paperclip ls /papers/bio_4f78753a6feb/figures/`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FIGURE_ID", type: "string", required: true, description: "Figure filename (e.g. fig1.tif, 657517v1_fig1.tif)" },
              { name: "QUESTION", type: "string", description: "Question about the image", default: '"Describe this figure in detail."' },
              { name: "--list", type: "flag", description: "List available figures for the current paper" },
            ]}
          />
          <Note>
            <InlineCode>ask-image</InlineCode> requires being inside a paper directory. From the CLI,
            wrap with <InlineCode>paperclip bash {"'"}cd /papers/ID/ && ask_image ...{"'"}</InlineCode>.
            When used via MCP, the agent{"'"}s session maintains the working directory automatically.
          </Note>
        </Section>

        {/* routines */}
        <Section id="routines" title="paperclip routines">
          <Callout>
            Discover, enable, and run guided analysis workflows. Routine enablement is
            stored on your account; workflow files are loaded remotely when needed.
          </Callout>
          <CodeBlock
            code={`# Discover routines available to this account
paperclip routines list
paperclip routines search "systematic review"

# Inspect and enable a routine
paperclip routines show paperclip-meta-analysis
paperclip routines enable paperclip-meta-analysis

# Load the enabled routine matching an agent's current task
paperclip routines route "meta-analysis of CAR-T efficacy"

# Run a trusted helper requested by the routine
paperclip routines run paperclip-meta-analysis status --dir ./car-t-analysis --json

# Turn off account-level routing for a routine
paperclip routines disable paperclip-meta-analysis`}
            language="bash"
          />
          <P>
            Routines are different from the core Paperclip skill.{" "}
            <InlineCode>paperclip install</InlineCode> installs the lightweight agent
            pointer, and <InlineCode>paperclip skill</InlineCode> loads core or domain
            documentation. <InlineCode>paperclip routines enable</InlineCode> only changes
            account-level workflow availability.
          </P>
          <Note>
            Trusted helpers run from checksum-verified temporary bundles and are removed
            after execution. Run routine commands from the authenticated host CLI, not from
            the virtual filesystem shell.
          </Note>
        </Section>

        {/* FILE OPERATIONS */}

        {/* cat */}
        <Section id="cat" title="paperclip cat">
          <Callout>
            Read file contents from the paper filesystem. Large files are automatically truncated with a section index.
          </Callout>
          <CodeBlock
            code={`# Read metadata
paperclip cat /papers/bio_4f78753a6feb/meta.json

# Read full text (first 100 lines by default)
paperclip cat /papers/bio_4f78753a6feb/content.lines

# Read with line numbers
paperclip cat -n /papers/bio_4f78753a6feb/content.lines

# Read first N lines
paperclip cat --lines 50 /papers/bio_4f78753a6feb/content.lines

# Read a specific line range
paperclip cat --lines 100-200 /papers/bio_4f78753a6feb/content.lines

# Read a specific section
paperclip cat /papers/bio_4f78753a6feb/sections/Methods.lines`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FILE", type: "string", required: true, description: "Path to the file to read" },
              { name: "-n", type: "flag", description: "Show line numbers" },
              { name: "--lines N", type: "int", description: "Show first N lines (overrides the 100-line default)" },
              { name: "--lines N-M", type: "range", description: "Show lines N through M" },
            ]}
          />
          <P>
            For large files, <InlineCode>cat</InlineCode> automatically shows the first 100
            lines plus a section index. Use <InlineCode>head</InlineCode>,{" "}
            <InlineCode>grep</InlineCode>, or <InlineCode>scan</InlineCode> for more targeted reads.
          </P>
        </Section>

        {/* head / tail */}
        <Section id="head-tail" title="paperclip head / tail">
          <Callout>Display the first or last lines of a file.</Callout>
          <CodeBlock
            code={`paperclip head -50 /papers/bio_4f78753a6feb/content.lines
paperclip tail -20 /papers/bio_4f78753a6feb/content.lines

# Short form (equivalent)
paperclip head -n 50 /papers/bio_4f78753a6feb/content.lines`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FILE", type: "string", required: true, description: "Path to the file" },
              { name: "-n N or -N", type: "int", description: "Number of lines to show", default: "10" },
            ]}
          />
        </Section>

        {/* ls */}
        <Section id="ls" title="paperclip ls">
          <Callout>List directory contents in the paper filesystem.</Callout>
          <CodeBlock
            code={`paperclip ls /papers/bio_4f78753a6feb/
# meta.json  content.lines  sections/  figures/  supplements/

paperclip ls /papers/bio_4f78753a6feb/sections/
# Introduction.lines  Methods.lines  Results.lines  Discussion.lines

# Long format with token estimates
paperclip ls -l /papers/bio_4f78753a6feb/`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "PATH", type: "string", description: "Directory to list", default: "current directory" },
              { name: "-l", type: "flag", description: "Long format (shows permissions, token estimates, titles)" },
              { name: "-a", type: "flag", description: "Show all entries (including hidden)" },
            ]}
          />
        </Section>

        {/* tree */}
        <Section id="tree" title="paperclip tree">
          <Callout>Display a directory tree.</Callout>
          <CodeBlock
            code={`paperclip tree /papers/bio_4f78753a6feb/
paperclip tree -L 2 /papers/bio_4f78753a6feb/`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "PATH", type: "string", description: "Directory to display", default: "current directory" },
              { name: "-L, --depth", type: "int", description: "Maximum depth", default: "3" },
            ]}
          />
        </Section>

        {/* wc */}
        <Section id="wc" title="paperclip wc">
          <Callout>Count lines, words, or characters in paper files.</Callout>
          <CodeBlock
            code={`paperclip wc -l /papers/bio_4f78753a6feb/content.lines
#      847 /papers/bio_4f78753a6feb/content.lines

paperclip wc -w /papers/bio_4f78753a6feb/content.lines`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FILE", type: "string", required: true, description: "File to count" },
              { name: "-l", type: "flag", description: "Count lines" },
              { name: "-w", type: "flag", description: "Count words" },
              { name: "-c, -m", type: "flag", description: "Count characters" },
            ]}
          />
          <P>With no flags, all three counts are shown.</P>
        </Section>

        {/* cd / pwd */}
        <Section id="cd" title="paperclip cd / pwd">
          <Callout>
            Navigate the virtual filesystem within <InlineCode>bash</InlineCode> chains.
            Entering a paper directory shows a summary with title and token count.
          </Callout>
          <CodeBlock
            code={`# cd + command chain (cd only persists within the bash call)
paperclip bash 'cd /papers/bio_4f78753a6feb/ && ls'
paperclip bash 'cd /papers/bio_4f78753a6feb/ && ask_image --list'
paperclip bash 'cd /papers/bio_4f78753a6feb/ && grep -i "kinase" content.lines'`}
            language="bash"
          />
          <P>
            <InlineCode>~</InlineCode> expands to <InlineCode>/papers</InlineCode>. From the CLI,{" "}
            <InlineCode>cd</InlineCode> is useful inside{" "}
            <InlineCode>paperclip bash {"'"}...{"'"}</InlineCode> chains to set context for commands
            that need a paper directory (like <InlineCode>ask_image</InlineCode>). When Paperclip
            is used as an MCP server, the agent{"'"}s session maintains directory state across calls
            automatically.
          </P>
        </Section>

        {/* TEXT PROCESSING */}

        {/* sort / uniq / cut / tr */}
        <Section id="sort" title="paperclip sort / uniq / cut / tr">
          <Callout>Standard text processing utilities, available as subcommands or in pipes.</Callout>
          <CodeBlock
            code={`# Sort, reverse, numeric, unique
paperclip bash 'grep "kinase" /papers/bio_abc123/content.lines | sort -u'

# Cut specific fields
paperclip bash 'grep "gene" /papers/bio_abc123/content.lines | cut -d: -f2'

# Translate characters
paperclip bash 'head -10 /papers/bio_abc123/content.lines | tr A-Z a-z'`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "sort", type: "cmd", description: "-r reverse, -n numeric, -u unique" },
              { name: "uniq", type: "cmd", description: "-c count occurrences, -d only duplicates, -u only unique" },
              { name: "cut", type: "cmd", description: "-d delimiter, -f field numbers" },
              { name: "tr", type: "cmd", description: "-d delete chars, -s squeeze repeats, or two charsets for translate" },
            ]}
          />
        </Section>

        {/* sed */}
        <Section id="sed" title="paperclip sed">
          <Callout>
            Stream editor (limited subset). Supports address ranges, substitution, and line deletion.
          </Callout>
          <CodeBlock
            code={`# Extract lines 50-100
paperclip sed -n '50,100p' /papers/bio_4f78753a6feb/content.lines

# Substitution
paperclip bash 'head -20 /papers/bio_abc123/content.lines | sed "s/CRISPR/crispr/g"'

# Delete matching lines
paperclip bash 'cat /papers/bio_abc123/content.lines | sed "/^$/d"'`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "EXPRESSION", type: "string", required: true, description: "sed expression (s///, N,Mp, /pattern/d, /pattern/!d)" },
              { name: "FILE", type: "string", description: "Input file (or pipe via stdin)" },
              { name: "-n", type: "flag", description: "Suppress default output (print only with p)" },
            ]}
          />
        </Section>

        {/* awk */}
        <Section id="awk" title="paperclip awk">
          <Callout>Pattern processing (limited subset).</Callout>
          <CodeBlock
            code={`# Print Nth field
paperclip bash 'grep "kinase" /papers/bio_abc123/content.lines | awk "{print \\$2}"'

# Custom delimiter
paperclip bash 'grep "kinase" /papers/bio_abc123/content.lines | awk -F: "{print \\$1}"'

# Print matching lines (like grep)
paperclip bash 'cat /papers/bio_abc123/content.lines | awk "/kinase/"'

# Line range
paperclip bash 'cat /papers/bio_abc123/content.lines | awk "NR>=5 && NR<=10"'`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "PROGRAM", type: "string", required: true, description: "Supported: /pattern/, /start/,/end/, {print $N}, NR>=N && NR<=M, {print NR, $0}" },
              { name: "-F", type: "string", description: "Field delimiter", default: "whitespace" },
            ]}
          />
        </Section>

        {/* jq */}
        <Section id="jq" title="paperclip jq">
          <Callout>Minimal JSON query tool. Supports dot-path access, keys, and length.</Callout>
          <CodeBlock
            code={`paperclip bash 'cat /papers/bio_4f78753a6feb/meta.json | jq .title'
paperclip bash 'cat /papers/bio_4f78753a6feb/meta.json | jq keys'`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FILTER", type: "string", required: true, description: "jq filter: ., .key, .key.subkey, keys, length" },
            ]}
          />
        </Section>

        {/* Pipes & bash */}
        <Section id="bash-pipes" title="Pipes & bash">
          <Callout>
            For compound expressions (pipes, <InlineCode>&&</InlineCode>, chains), wrap
            in <InlineCode>paperclip bash {"'"}...{"'"}</InlineCode>. Single commands don{"'"}t
            need this wrapper.
          </Callout>
          <CodeBlock
            code={`# Count grep matches
paperclip bash 'grep -ic "p53" /papers/bio_4f78753a6feb/content.lines'

# Chain search → grep
paperclip bash 'search "CRISPR delivery" -n 10 | grep "AAV"'

# Write results to scratch space
paperclip bash 'grep -i "kinase" /papers/ > /.gxl/kinase_hits.txt'

# Multi-step pipeline
paperclip bash 'head -100 /papers/bio_abc123/content.lines | grep -i "method" | wc -l'`}
            language="bash"
          />
          <P>
            Inside <InlineCode>bash</InlineCode>, you can use any allowlisted command without
            the <InlineCode>paperclip</InlineCode> prefix. Pipes (<InlineCode>|</InlineCode>) and
            chains (<InlineCode>&&</InlineCode>) work as expected.
          </P>
          <Note>
            Python is also available via <InlineCode>paperclip python "print(2+2)"</InlineCode>{" "}
            or <InlineCode>paperclip python3 script.py</InlineCode>, executed in a sandboxed environment.
          </Note>
        </Section>

        {/* RESULTS & OUTPUT */}

        {/* results */}
        <Section id="results" title="paperclip results">
          <Callout>View, browse, and export saved search and map results.</Callout>
          <P>
            Every <InlineCode>search</InlineCode>, <InlineCode>lookup</InlineCode>, and{" "}
            <InlineCode>map</InlineCode> command saves its output with a result ID. Use{" "}
            <InlineCode>results</InlineCode> to access them later.
          </P>
          <CodeBlock
            code={`# Interactive picker (arrow keys to navigate, enter to save)
paperclip results

# View a specific result
paperclip results s_4a2b61f6

# View a map result
paperclip results m_ec2c9cc9

# Non-interactive list
paperclip results --list

# Export to CSV
paperclip results s_4a2b61f6 --save results.csv

# Export to plain text
paperclip results s_4a2b61f6 --save results.txt`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "RESULT_ID", type: "string", description: "Specific result to view (s_xxx for search, m_xxx for map)" },
              { name: "--list", type: "flag", description: "Non-interactive list of all saved results" },
              { name: "--save PATH", type: "string", description: "Export to file. .csv exports structured data; other extensions export plain text." },
            ]}
          />
          <Tip>
            With no arguments, <InlineCode>paperclip results</InlineCode> opens an interactive TUI
            picker where you can navigate with arrow keys, paginate with n/p, and press enter to
            select and save.
          </Tip>
        </Section>

        {/* redirection */}
        <Section id="redirection" title="Redirection">
          <Callout>
            Append <InlineCode>&gt;</InlineCode> to any Paperclip command to save its output to a local file.
          </Callout>
          <P>Standard shell redirection captures command output to your local filesystem.</P>
          <CodeBlock
            code={`# Save search results locally
paperclip search -s papers "CRISPR delivery" -n 10 > crispr_results.txt

# Save a paper's metadata
paperclip cat /papers/bio_4f78753a6feb/meta.json > meta.json

# Save SQL query output
paperclip sql "SELECT title, doi FROM documents WHERE authors ILIKE '%Doudna%' LIMIT 20" > doudna_papers.txt

# Append to an existing file
paperclip search -s papers "protein folding" >> research_log.txt

# Download a figure
paperclip cat /papers/bio_abc123/figures/fig1.tif > fig1.tif`}
            language="bash"
          />
          <P>
            When <InlineCode>cat</InlineCode> encounters a binary file (image/figure), Paperclip
            generates a short-lived signed URL and streams the bytes directly &mdash; no intermediate
            text encoding.
          </P>
        </Section>

        {/* PAPER REPOS */}

        <Section id="repos-overview" title="Paper repos · Overview">
          <Callout>
            <InlineCode>paperclip git</InlineCode> is the preferred name for paper
            repositories. <InlineCode>paperclip repo</InlineCode> and{" "}
            <InlineCode>paperclip repos</InlineCode> are the same command group and are
            fully supported. A repo is git-like version control for a curated literature
            collection. As an agent reads, it <InlineCode>add</InlineCode>s the claims it
            intends to cite, <InlineCode>commit</InlineCode> verifies each claim against
            the full source text (dropping unsupported ones), and{" "}
            <InlineCode>branch</InlineCode> / <InlineCode>merge</InlineCode> isolate side
            questions — the same verbs you already use for code, pointed at the literature.
          </Callout>
          <P>
            Under the hood a repo is a virtual filesystem of lightweight pointers into the
            shared paper store, so <InlineCode>add</InlineCode>, <InlineCode>commit</InlineCode>,
            and <InlineCode>merge</InlineCode> move pointers (not gigabytes) and complete in
            milliseconds. Every action is recorded, so the corpus is auditable with{" "}
            <InlineCode>git log</InlineCode> and <InlineCode>git history</InlineCode>,
            and reproducible by anyone with access.
          </P>
          <h3 className="text-base font-medium text-zinc-900 mt-6 mb-3">The core workflow</h3>
          <CodeBlock
            code={`# 1. Create (or switch into) a repo — it becomes the active, sticky repo
paperclip git init kras-resistance "KRAS G12C resistance mechanisms"

# 2. Search is auto-scoped to the repo; add papers with a verifiable claim
paperclip search -s papers "KRAS G12C adagrasib resistance" -n 10
paperclip git add PMC10150846 "Adagrasib ORR 42.9% in NSCLC" --lines L210-L214

# 3. Commit — every unverified claim is checked against the full source text
paperclip git commit -m "first-line resistance papers"

# 4. Explore a side question on a branch, then fold it back in
paperclip git branch safety-signals
paperclip git add PMC8834120 "Grade 3+ hepatotoxicity in 5.2%" --lines L88
paperclip git commit -m "hepatotoxicity signals"
paperclip git checkout main
paperclip git merge safety-signals

# 5. Audit and export
paperclip git log
paperclip git export bibtex -o kras.bib`}
            language="bash"
          />
          <h3 className="text-base font-medium text-zinc-900 mt-6 mb-3">Which repo a command targets</h3>
          <P>
            Repo selection follows a strict precedence so concurrent agents never collide:
          </P>
          <ParamTable
            params={[
              { name: "--repo <name>", type: "flag", description: "Global flag (before the subcommand) — targets this repo for one command without changing sticky state. Highest precedence. Preferred. -f and --folder still work." },
              { name: "PAPERCLIP_REPO", type: "env", description: "Scopes an entire session to one repo. Wins over the sticky checkout." },
              { name: "checkout", type: "sticky", description: "The last `paperclip git checkout <name>` is remembered in ~/.paperclip/repos/.active for interactive use." },
              { name: "--repo-only", type: "flag", description: "Restrict search/map to the active repo's curated papers. Default search runs against the full corpus." },
              { name: "--corpus", type: "flag", description: "Force a full-corpus search/map even when --repo-only is set." },
            ]}
          />
          <Tip>
            By default <InlineCode>search</InlineCode> and <InlineCode>map</InlineCode> run
            against the full corpus even when a repo is active — pass{" "}
            <InlineCode>--repo-only</InlineCode> to scope them to the active repo's curated
            papers. <InlineCode>search</InlineCode> still accepts <InlineCode>--add</InlineCode>{" "}
            (auto-add the results) and <InlineCode>--commit "msg"</InlineCode> (add then commit)
            for one-line curation.
          </Tip>
        </Section>

        {/* init */}
        <Section id="init" title="paperclip git init">
          <Callout>
            Create a new paper repo and make it the active (sticky) repo. The name is
            normalized — lowercased with spaces converted to hyphens.
          </Callout>
          <CodeBlock
            code={`paperclip git init kras-resistance "KRAS G12C resistance mechanisms"
paperclip git init my-review
paperclip git init lit-review --from-library            # seed with all matched library papers
paperclip git init subset --from-library -s "CRISPR"    # seed with matching library papers`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "NAME", type: "string", required: true, description: "Repo name (normalized to lowercase-with-hyphens). Must be unique." },
              { name: "DESCRIPTION", type: "string", description: "Optional free-text description." },
              { name: "--from-library", type: "flag", description: "Seed the new repo with matched (corpus-resolved) papers from your personal library." },
              { name: "-s, --search", type: "string", description: "With --from-library, only seed library papers whose title/author/journal match this keyword." },
            ]}
          />
        </Section>

        {/* repo */}
        <Section id="repo" title="paperclip git">
          <Callout>
            List your repos, or show details for one. Active repo is marked.{" "}
            <InlineCode>paperclip repo</InlineCode> and{" "}
            <InlineCode>paperclip repos</InlineCode> run the same command.
          </Callout>
          <CodeBlock
            code={`paperclip git                  # list 10 most recent repos (active first)
paperclip git -n 0             # list all repos
paperclip git info kras-resistance   # papers, branches, and commit history for one repo`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "-n", type: "int", description: "Number of repos to list (0 = all).", default: "10" },
              { name: "info <name>", type: "subcommand", description: "Show a single repo's papers, branches, and commits." },
            ]}
          />
        </Section>

        {/* checkout */}
        <Section id="checkout" title="paperclip git checkout">
          <Callout>
            Switch the active repo, switch branches within the active repo, or deactivate.{" "}
            Prefer <InlineCode>checkout</InlineCode>. <InlineCode>git switch</InlineCode> is
            also accepted.
          </Callout>
          <CodeBlock
            code={`paperclip git checkout kras-resistance   # activate a repo (sticky)
paperclip git checkout main              # switch to a branch (when the name matches a branch)
paperclip git checkout -                 # deactivate — commands run against the full corpus again
paperclip git switch main                # also accepted; same as checkout`}
            language="bash"
          />
          <P>
            If the active repo has a branch whose name matches the argument, it switches
            branches; otherwise it switches repos. Prefer{" "}
            <InlineCode>--repo</InlineCode> to override sticky selection for one command.{" "}
            <InlineCode>-f</InlineCode> / <InlineCode>--folder</InlineCode> still work.{" "}
            <InlineCode>PAPERCLIP_REPO</InlineCode> also overrides sticky selection.
          </P>
          <ParamTable
            params={[
              { name: "NAME", type: "string", required: true, description: "Repo name, branch name, or '-' to deactivate." },
            ]}
          />
        </Section>

        {/* add */}
        <Section id="add" title="paperclip git add">
          <Callout>
            Add papers to the active repo, optionally with a verifiable claim. Append-only —
            call it again with the same ID and a different claim to attach multiple claims.
          </Callout>
          <CodeBlock
            code={`paperclip git add PMC123 bio_456
paperclip git add PMC123 "Hypertonic saline reduces cold duration by 1.9 days"
paperclip git add PMC123 "Saline shortens URTI by 1.5 days" --lines L45-L52
paperclip git add PMC123 "ORR 32.4%" --commit "ADC efficacy"   # add then commit
paperclip git add --from-library                                # add matched library papers
paperclip git add --from-library -s "proteomics"`}
            language="bash"
          />
          <P>
            The first argument(s) that look like document IDs (e.g. <InlineCode>PMC…</InlineCode>,{" "}
            <InlineCode>bio_…</InlineCode>, <InlineCode>fda_…</InlineCode>,{" "}
            <InlineCode>tri_…</InlineCode>) are treated as paper IDs; a trailing free-text
            argument becomes the claim, stored as an annotation to be checked on{" "}
            <InlineCode>commit</InlineCode>. DOIs and citation counts are fetched
            best-effort for newly added papers.
          </P>
          <ParamTable
            params={[
              { name: "PAPER_IDS", type: "string[]", description: "One or more document IDs. A trailing non-ID argument is stored as the claim." },
              { name: "--lines", type: "string", description: "Line-range hint for the claim, e.g. L45-L52 or L45,L120. Used to focus verification." },
              { name: "--commit", type: "string", description: "Auto-commit (with this message) immediately after adding." },
              { name: "--from-library", type: "flag", description: "Add matched (corpus-resolved) papers from your personal library instead of explicit IDs." },
              { name: "-s, --search", type: "string", description: "With --from-library, filter library papers by keyword." },
              { name: "--repo", type: "string", description: "Target repo for this add (same as the global --repo flag). Preferred. -f and --folder still work." },
            ]}
          />
        </Section>

        {/* remove */}
        <Section id="remove" title="paperclip git remove">
          <Callout>Remove a paper (and its claims) from the active repo.</Callout>
          <CodeBlock
            code={`paperclip git remove PMC8834120
paperclip git remove 4402004380   # e.g. dropping a predatory-journal hit during screening`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "PAPER_ID", type: "string", required: true, description: "Document ID of the paper to remove from the active repo." },
            ]}
          />
        </Section>

        {/* commit */}
        <Section id="commit" title="paperclip git commit">
          <Callout>
            Snapshot the current repo state. Before committing, each paper's claim is verified
            against the paper's full text by a dedicated subagent, in parallel — unsupported
            claims are flagged so you can correct or drop them.
          </Callout>
          <CodeBlock
            code={`paperclip git commit -m "initial resistance papers"
paperclip git commit -m "Found 3 phase-2 trials" -r "Added spleen LNP data, still need phase-3."
paperclip git commit -m "quick snapshot" --no-verify`}
            language="bash"
          />
          <P>
            The commit message is the audit trail — explain what was added or removed and why,
            what you found, and what is still missing. Verification results (✓ supported / ✗
            not supported, with evidence) are saved and surfaced by{" "}
            <InlineCode>paperclip git status</InlineCode>.
          </P>
          <ParamTable
            params={[
              { name: "-m, --message", type: "string", required: true, description: "Commit message describing the change and findings." },
              { name: "-r, --rationale", type: "string", description: "Extended rationale, appended to the message." },
              { name: "--verify / --no-verify", type: "flag", description: "Verify each claim against its source before committing.", default: "--verify" },
            ]}
          />
        </Section>

        {/* status */}
        <Section id="status" title="paperclip git status">
          <Callout>
            Show the active repo's current state — papers, attached claims, and the
            verification marks from the last <InlineCode>commit</InlineCode>. Like{" "}
            <InlineCode>git status</InlineCode>.
          </Callout>
          <CodeBlock
            code={`paperclip git status

# ○ = claim not yet verified (run: paperclip git commit -m "…")
# ✓ = claim supported by source   ✗ = claim not supported`}
            language="bash"
          />
        </Section>

        {/* log */}
        <Section id="log" title="paperclip git log">
          <Callout>
            Show the commit history for the active repo — SHAs, messages, and paper counts.
            Like <InlineCode>git log</InlineCode>.
          </Callout>
          <CodeBlock
            code={`paperclip git log
paperclip git log -n 5
paperclip git log --branch safety-concerns
paperclip git log --json`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "-n, --limit", type: "int", description: "Maximum number of commits to show.", default: "20" },
              { name: "-b, --branch", type: "string", description: "Show the log for a specific branch." },
              { name: "--json", type: "flag", description: "Output the commit log as JSON." },
            ]}
          />
        </Section>

        {/* history */}
        <Section id="history" title="paperclip git history">
          <Callout>
            The full, replayable command audit trail — every search, add, removal, commit, and
            merge, in order. This is what lets another agent resume an in-progress repo.
          </Callout>
          <CodeBlock
            code={`paperclip git history
paperclip git history -t search_papers   # filter by tool name
paperclip git history -n 10              # results per page
paperclip git history --json`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "-t, --tool", type: "string", description: "Filter by tool name (e.g. search_papers)." },
              { name: "-p, --page", type: "int", description: "Page number.", default: "1" },
              { name: "-n, --per-page", type: "int", description: "Results per page.", default: "20" },
              { name: "--json", type: "flag", description: "Output the history as JSON." },
            ]}
          />
        </Section>

        {/* branch */}
        <Section id="branch" title="paperclip git branch">
          <Callout>
            List branches, or create and switch to a new one. A new branch forks the current
            branch with all of its papers — a clean workspace for a side question.
          </Callout>
          <CodeBlock
            code={`paperclip git branch                   # list branches (active marked)
paperclip git branch clinical-data     # create + switch to a new branch`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "NAME", type: "string", description: "Branch to create and switch to. Omit to list branches." },
            ]}
          />
        </Section>

        {/* merge */}
        <Section id="merge" title="paperclip git merge">
          <Callout>
            Merge papers from another branch into the current branch (a union). If both
            branches hold the same paper, the current branch's version is kept. Records a merge
            commit.
          </Callout>
          <CodeBlock
            code={`paperclip git checkout main
paperclip git merge clinical-data`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "SOURCE_BRANCH", type: "string", required: true, description: "Branch whose papers are merged into the current branch." },
            ]}
          />
        </Section>

        {/* export (repo) */}
        <Section id="repo-export" title="paperclip git export">
          <Callout>
            Export the active repo as a bibliography. Writes to stdout by default, or to a file
            with <InlineCode>-o</InlineCode>.
          </Callout>
          <CodeBlock
            code={`paperclip git export bibtex > kras.bib
paperclip git export bib -o kras.bib       # "bib" is an alias for bibtex
paperclip git export ris -o kras.ris
paperclip git export markdown -o review.md  # "md" is an alias for markdown
paperclip git export csv -o papers.csv`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "FORMAT", type: "string", required: true, description: "One of: bibtex (bib), ris, markdown (md), csv." },
              { name: "-o, --output", type: "string", description: "Write to this file instead of stdout." },
            ]}
          />
        </Section>

        {/* citations */}
        <Section id="citations" title="paperclip git citations">
          <Callout>
            Citation counts and relationships for the repo's papers, sourced from Semantic
            Scholar. Defaults to counts; use <InlineCode>--graph</InlineCode> for relationships.
          </Callout>
          <CodeBlock
            code={`paperclip git citations                          # counts for all repo papers
paperclip git citations --refresh                # force a fresh lookup from Semantic Scholar
paperclip git citations --graph                  # citation relationships between repo papers
paperclip git citations PMC9170150 PMC11748609   # specific papers`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "PAPER_IDS", type: "string[]", description: "Specific paper IDs. Defaults to all papers in the active repo." },
              { name: "--graph", type: "flag", description: "Show citation relationships between the papers instead of counts." },
              { name: "--refresh", type: "flag", description: "Force a fresh lookup from Semantic Scholar (bypass cache)." },
            ]}
          />
        </Section>

        {/* CONFIGURATION */}

        {/* auth */}
        <Section id="auth" title="Authentication">
          <P>
            Paperclip can authenticate with <strong>OAuth</strong> (browser sign-in) or an{" "}
            <strong>API key</strong> for non-interactive use. OAuth credentials live at{" "}
            <InlineCode>~/.paperclip/credentials.json</InlineCode> and refresh automatically. API keys
            are never written to disk by the CLI; pass them per run or via an environment variable (see{" "}
            <button
              type="button"
              onClick={() => handleNavigate("api-keys")}
              className="text-zinc-700 underline underline-offset-2 hover:text-zinc-900 p-0 border-0 bg-transparent cursor-pointer text-base font-inherit leading-[1.75]"
            >
              API keys
            </button>
            ).
          </P>
          <CodeBlock
            code={`paperclip login     # opens browser for sign-in (OAuth)
paperclip config    # check server URL + OAuth status (do not pass --api-key)
paperclip logout    # clear OAuth credentials and local config`}
            language="bash"
          />
          <P>
            For servers, CI, or any environment without a browser, set{" "}
            <InlineCode>PAPERCLIP_API_KEY</InlineCode> or use the global{" "}
            <InlineCode>--api-key</InlineCode> flag before the subcommand:
          </P>
          <CodeBlock
            code={`export PAPERCLIP_API_KEY='gxl_...'
paperclip search -s papers "CRISPR delivery" -n 5

# Equivalent one-off:
paperclip --api-key 'gxl_...' search -s papers "CRISPR delivery" -n 5`}
            language="bash"
          />
          <Note>
            The <InlineCode>login</InlineCode> and <InlineCode>config</InlineCode> commands only apply to
            OAuth and local settings; do not pass <InlineCode>--api-key</InlineCode> to them.
          </Note>
        </Section>

        {/* api-keys */}
        <Section id="api-keys" title="API keys">
          <P>
            Create, rotate, and revoke keys from the Paperclip web app:{" "}
            <Link to="/keys" className="text-zinc-700 underline underline-offset-2 hover:text-zinc-900">
              API keys
            </Link>
            . The CLI sends the key on every request as the <InlineCode>X-API-Key</InlineCode> header.
          </P>
          <h3 className="text-base font-medium text-zinc-900 mb-3">Ways to provide the key</h3>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-disc">
            <li>
              <strong>Environment variable</strong> —{" "}
              <InlineCode>PAPERCLIP_API_KEY</InlineCode> (recommended for scripts and CI).
            </li>
            <li>
              <strong>Global flag</strong> —{" "}
              <InlineCode>paperclip --api-key KEY ...</InlineCode> or{" "}
              <InlineCode>paperclip --api-key=KEY ...</InlineCode> before the subcommand.
            </li>
            <li>
              For passthrough filesystem commands (e.g. <InlineCode>grep</InlineCode>,{" "}
              <InlineCode>cat</InlineCode>), <InlineCode>--api-key</InlineCode> may also appear after the
              subcommand; the CLI strips it before running the remote command.
            </li>
          </ul>
          <CodeBlock
            code={`# CI / production job
export PAPERCLIP_API_KEY="$SECRET"
paperclip search -s papers "gene therapy" -n 20

# Ad-hoc (avoid shell history: prefer env or a secret store)
paperclip --api-key "$PAPERCLIP_API_KEY" grep -i "AAV" /papers/`}
            language="bash"
          />
          <Tip>
            If neither OAuth credentials nor an API key is available and stdin is not a TTY, the CLI
            exits with an error — set <InlineCode>PAPERCLIP_API_KEY</InlineCode> or run{" "}
            <InlineCode>paperclip login</InlineCode> on a machine with a browser.
          </Tip>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">MCP server with API key</h3>
          <P>
            To authenticate the MCP server in Cursor (or any MCP client that supports headers),
            add an <InlineCode>X-API-Key</InlineCode> header to your <InlineCode>mcp.json</InlineCode>:
          </P>
          <CodeBlock
            code={`{\n  "mcpServers": {\n    "paperclip": {\n      "url": "https://paperclip.gxl.ai/mcp",\n      "type": "http",\n      "headers": {\n        "X-API-Key": "gxl_your_api_key_here"\n      }\n    }\n  }\n}`}
            language="json"
          />
        </Section>

        {/* http-api */}
        <Section id="http-api" title="HTTP API">
          <P>
            Paperclip exposes a JSON HTTP API so agents can search and retrieve identified
            papers without parsing CLI output. Authenticate with{" "}
            <InlineCode>X-API-Key</InlineCode> or{" "}
            <InlineCode>Authorization: Bearer gxl_…</InlineCode>. Base URL:{" "}
            <InlineCode>https://paperclip.gxl.ai/api/v1</InlineCode>. OpenAPI:{" "}
            <InlineCode>/api/v1/openapi.json</InlineCode>.
          </P>
          <P>
            Default search corpus is <InlineCode>abstracts</InlineCode> (title + abstract
            leftover). <InlineCode>pmc</InlineCode> / <InlineCode>papers</InlineCode> are a
            different full-text collection — pass <InlineCode>sources</InlineCode> explicitly.
            Every hit is a citable paper: <InlineCode>id</InlineCode>,{" "}
            <InlineCode>title</InlineCode>, <InlineCode>pmid</InlineCode>,{" "}
            <InlineCode>doi</InlineCode>, <InlineCode>year</InlineCode>,{" "}
            <InlineCode>source</InlineCode>.
          </P>
          <CodeBlock
            code={`curl -sS -X POST https://paperclip.gxl.ai/api/v1/search \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: $PAPERCLIP_API_KEY" \\
  -d '{
    "query": "Allgrove syndrome",
    "num_results": 50,
    "filters": { "max_year": 2016 }
  }'`}
            language="bash"
          />
          <CodeBlock
            code={`curl -sS -X POST https://paperclip.gxl.ai/api/v1/lookup \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: $PAPERCLIP_API_KEY" \\
  -d '{ "pmid": "27048983" }'

curl -sS https://paperclip.gxl.ai/api/v1/documents/pmid:27048983 \\
  -H "X-API-Key: $PAPERCLIP_API_KEY"`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "query / queries", type: "string | string[]", description: "One query, or up to 6 phrasings fanned out together: candidate pools are fused (RRF) and deduped by PMID/DOI/title." },
              { name: "rerank_query", type: "string", description: "Deprecated: accepted and ignored (returns a warnings entry and a Deprecation header)." },
              { name: "expand", type: "bool", description: "Let an LLM add synonyms, gene symbols and sub-topic phrasings to the fan-out. Better recall on broad questions; slower.", default: "false" },
              { name: "num_results / limit", type: "int", description: "Papers to return (1–200). BM25 and vector retrieval run in parallel and are fused; ranking reorders, it does not drop.", default: "50" },
              { name: "sources", type: "string[]", description: "Default abstracts. pmc/papers = full-text.", default: "[\"abstracts\"]" },
              { name: "filters.min_year / max_year / year", type: "int", description: "Inclusive year bounds applied in the index. Hits with unknown year are kept." },
              { name: "ranking", type: "string", description: "Deprecated: accepted and ignored; search is always hybrid." },
              { name: "recency", type: "string", description: "off (full corpus) or boost (last ~2 years). Year bounds override boost.", default: "off" },
              { name: "lookup pmid / doi / year / …", type: "string | int", description: "POST /lookup retrieves the same paper object by identifier or metadata field." },
            ]}
          />
          <Note>
            Errors are <InlineCode>{"{ error: { type, message, request_id } }"}</InlineCode>, not ANSI.
          </Note>
        </Section>

        {/* config */}
        <Section id="config" title="Config">
          <P>
            Configuration is stored in <InlineCode>~/.paperclip/config.json</InlineCode>.
          </P>
          <CodeBlock
            code={`paperclip config                              # show current config
paperclip config --url http://localhost:8002   # set server URL (local dev)`}
            language="bash"
          />
          <ParamTable
            params={[
              { name: "--url", type: "string", description: "Set the server base URL" },
              { name: "--show", type: "flag", description: "Show current configuration (default when no flags)" },
              { name: "PAPERCLIP_BASE_URL", type: "env", description: "Env var to override the server base URL" },
              { name: "PAPERCLIP_API_KEY", type: "env", description: "API key for non-interactive auth (same as global --api-key); not stored locally by the CLI" },
              { name: "PAPERCLIP_CONFIG_DIR", type: "env", description: "Env var to override the config directory", default: "~/.paperclip" },
            ]}
          />
        </Section>

        <Section id="rate-limits" title="Rate limits">
          <P>
            Limits are per account. They are shared across every session, machine, CLI process,
            SDK client, and MCP connection on that account. A teammate&apos;s batch job uses the
            same budget as your interactive session.
          </P>
          <table className="w-full text-sm border border-zinc-200 rounded-lg overflow-hidden">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-zinc-700">Limit</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-700">Value</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-700">Scope</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              <tr>
                <td className="px-3 py-2">Request rate</td>
                <td className="px-3 py-2">120 requests / second</td>
                <td className="px-3 py-2">All commands</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Concurrent short commands</td>
                <td className="px-3 py-2">10 in flight</td>
                <td className="px-3 py-2">search, lookup, grep, cat, sql, and other fast commands</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Concurrent long commands</td>
                <td className="px-3 py-2">8 in flight</td>
                <td className="px-3 py-2">map, verify, and search-workflow (separate from the short pool)</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Daily map / verify</td>
                <td className="px-3 py-2">100 / day</td>
                <td className="px-3 py-2">Resets at midnight UTC</td>
              </tr>
            </tbody>
          </table>
          <P>
            A 429 response means a limit was hit. The Python SDK raises{" "}
            <InlineCode>RateLimitError</InlineCode> so a caller can wait and retry. The CLI retries
            with backoff and can look like a slow query while it waits. There is no endpoint that
            reads remaining quota without spending a call.
          </P>
          <P>
            Paid plans can raise these limits. Contact{" "}
            <a href="mailto:support@gxl.ai" className={docTextLinkClass}>
              support@gxl.ai
            </a>
            .
          </P>
          <Note>
            For scripts and agents, use the Python SDK. Each CLI process pays a startup and auth
            cost that can exceed the query time. The SDK keeps a session and does not.
          </Note>
        </Section>

        {/* skill-install */}
        <Section id="skill-install" title="Skill install">
          <P>
            Install the Paperclip skill so your coding agent (Claude Code, Codex, Cursor) knows
            how to use Paperclip automatically.
          </P>
          <CodeBlock
            code={`# Via the CLI (interactive agent picker)
paperclip install

# Install to a specific project directory
paperclip install --dir /path/to/project

# Via npm/npx (no Python required)
npx gxl-paperclip
npx gxl-paperclip --all      # skip interactive prompt
npx gxl-paperclip --cursor   # Cursor only`}
            language="bash"
          />
          <Tip>
            The skill file is fetched from the server and stays up to date. It also auto-refreshes
            on <InlineCode>paperclip login</InlineCode>.
          </Tip>
          <P>
            <InlineCode>paperclip skill</InlineCode> loads the current core instructions.
            Add a domain name, such as <InlineCode>paperclip skill patents</InlineCode>, to
            load a domain reference. Guided workflows are managed separately as routines.
          </P>
          <CodeBlock
            code={`# Discover and enable guided workflows
paperclip routines list
paperclip routines search "systematic review"
paperclip routines enable paperclip-meta-analysis

# Load an enabled routine when its trigger matches
paperclip routines route "meta-analysis"
paperclip routines show paperclip-meta-analysis`}
            language="bash"
          />
          <Tip>
            Enabling a routine updates your account. It does not install workflow files in
            the project.
          </Tip>
        </Section>

        {/* update */}
        <Section id="update" title="Update">
          <P>
            Update Paperclip to the latest version. The CLI also performs silent background update
            checks (every 4 hours).
          </P>
          <CodeBlock
            code={`paperclip update
# Current version: ${PAPERCLIP_DOCUMENTED_VERSION}
# Checking for updates...
# ✓ Already up to date (v${PAPERCLIP_DOCUMENTED_VERSION})`}
            language="bash"
          />
          <P>
            If an update is available, <InlineCode>paperclip</InlineCode> (with no arguments) will
            show a hint in the dashboard.
          </P>
        </Section>

        {/* filesystem */}
        <Section id="filesystem" title="Filesystem layout">
          <P>
            Each paper lives at <InlineCode>/papers/&lt;id&gt;/</InlineCode> with the following structure:
          </P>
          <CodeBlock
            code={`meta.json        — title, authors, doi, date, abstract, journal (JSON)
content.lines    — full text, line-numbered: L<n>: <text>
sections/        — named section files (Introduction.lines, Methods.lines, …)
figures/         — figure files (PMC papers)
supplements/     — supplementary files (PMC papers)`}
            language="text"
          />
          <P>Paper IDs use prefixes by source:</P>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-disc">
            <li><InlineCode>bio_</InlineCode> &mdash; bioRxiv papers</li>
            <li><InlineCode>med_</InlineCode> &mdash; medRxiv papers</li>
            <li><InlineCode>PMC</InlineCode> &mdash; PubMed Central papers (e.g. <InlineCode>PMC12345678</InlineCode>)</li>
            <li><InlineCode>arx_</InlineCode> &mdash; arXiv papers</li>
          </ul>
          <P>Regulatory documents, clinical trials, and patents use their own virtual directory paths:</P>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-disc">
            <li><InlineCode>/fda/us/</InlineCode> &mdash; US FDA documents (NDA, BLA identifiers)</li>
            <li><InlineCode>/fda/jp/</InlineCode> &mdash; Japan PMDA reviews</li>
            <li><InlineCode>/fda/eu/</InlineCode> &mdash; EU EPAR reports</li>
            <li><InlineCode>/clinicaltrials/us/</InlineCode> &mdash; ClinicalTrials.gov (NCT identifiers)</li>
            <li><InlineCode>/clinicaltrials/cn/</InlineCode> &mdash; ChiCTR (Chinese registry)</li>
            <li><InlineCode>/clinicaltrials/jp/</InlineCode> &mdash; UMIN + JRCT (Japanese registries)</li>
            <li><InlineCode>/clinicaltrials/eu/</InlineCode> &mdash; EudraCT + CTIS + ISRCTN (European registries)</li>
            <li><InlineCode>/clinicaltrials/intl/</InlineCode> &mdash; All registries + WHO ICTRP (India, Iran, Australia/NZ, Germany, Netherlands, Korea, Thailand, Brazil, Africa, Peru, Cuba, Sri Lanka, Lebanon)</li>
            <li><InlineCode>/patents/{"{publication_number}"}/</InlineCode> &mdash; global patent metadata, with full-text claims, descriptions, and compound annotations when available</li>
          </ul>
          <P>Biological databases are unified under a single path:</P>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-disc">
            <li><InlineCode>/proteins/{"{accession}"}/</InlineCode> &mdash; UniProt proteins with linked PDB structures and ChEMBL bioactivities (e.g. <InlineCode>/proteins/P42336/</InlineCode>)</li>
          </ul>
          <P>
            Query across UniProt, PDB, and ChEMBL with <InlineCode>sql -s uniprot</InlineCode>,{" "}
            <InlineCode>sql -s pdb</InlineCode>, or <InlineCode>sql -s chembl</InlineCode> &mdash; joins are pre-computed so one query can span all three.
          </P>
          <P>
            <strong>Scratch space:</strong> <InlineCode>/.gxl/</InlineCode> is a writable directory
            for session files, map outputs, and temporary data.
          </P>
          <Note>
            <InlineCode>content.lines</InlineCode> files can be very long. Always use{" "}
            <InlineCode>head -N</InlineCode> to paginate rather than <InlineCode>cat</InlineCode>{" "}
            for large papers.
          </Note>
        </Section>

        {/* ── Clipboard ─────────────────────────────────────── */}
        <Section id="clipboard-overview" title="Clipboard · Overview">
          <P>
            Your clipboard at <InlineCode>/clipboard/</InlineCode> is your personal, private document
            store. Upload PDFs from your machine, save papers from the public corpus, import a
            bibliography, or fetch a paywalled article through your browser session—everything becomes
            searchable, greppable, and citable, just like the 3.4M papers in the public corpus.
          </P>
          <P>
            Each uploaded document gets a unique ID (e.g. <InlineCode>usr_c1f9c5eac891</InlineCode>) and is
            parsed into the same structured layout as the corpus—<InlineCode>meta.json</InlineCode>,{" "}
            <InlineCode>content.lines</InlineCode> (line-numbered full text), <InlineCode>sections/</InlineCode>,
            and <InlineCode>figures/</InlineCode>—so <InlineCode>cat</InlineCode>, <InlineCode>grep</InlineCode>,{" "}
            <InlineCode>search</InlineCode>, <InlineCode>map</InlineCode>, and <InlineCode>ask-image</InlineCode>{" "}
            all work on your own documents unchanged.
          </P>
          <P>
            Clipboards are isolated per user with private search indexes—no one else can see or search your
            documents. You can grant teammates access to individual folders (see{" "}
            <InlineCode>share</InlineCode> below).
          </P>
        </Section>

        <Section id="clipboard-upload" title="Clipboard · Adding documents">
          <P>Upload local PDFs—a single file or an entire folder tree:</P>
          <CodeBlock code={`# Upload a folder of PDFs (recursive)\npaperclip cp ~/my_papers/\n\n# Upload a single PDF\npaperclip cp paper.pdf\n\n# Upload into a specific clipboard folder\npaperclip cp ~/data/ /clipboard/research/`} />
          <P>
            Files are parsed in parallel and available immediately. Duplicates (by content hash) are
            skipped automatically—uploading the same file twice is a no-op.
          </P>
          <P>Save a paper from the public corpus as a zero-copy link (no storage cost):</P>
          <CodeBlock code={`# Link a corpus paper (also works for /fda/ and /trials/)\npaperclip cp /papers/PMC7194329 /clipboard/my-review/\n\n# Link an existing clipboard doc into another folder\npaperclip cp /clipboard/usr_c1f9c5eac891 /clipboard/archive/`} />
          <Note>
            To save a paper you found via search, use <InlineCode>cp /papers/&lt;id&gt; /clipboard/…</InlineCode>—
            not <InlineCode>import &lt;id&gt;</InlineCode>, which fetches the paper&apos;s <em>references</em>, not the paper.
            Corpus links are references, not copies: reading <InlineCode>content.lines</InlineCode> proxies to the original.
          </Note>
          <P>Fetch a paywalled or institutional paper using your browser cookies, and import bibliographies:</P>
          <CodeBlock code={`# Download via your Chrome session and add to clipboard\npaperclip fetch https://www.nature.com/articles/s41586-023-05724-2\npaperclip fetch 10.1038/s41586-023-05724-2 --into /clipboard/my-review/\n\n# Import a .bib/.ris — each citation becomes a folder\n# (corpus link when found, else citation metadata)\npaperclip import refs.bib --into /clipboard/thesis-refs/`} />
          <P>
            Persist generated files (analysis artifacts, reports) with{" "}
            <InlineCode>upload --into</InlineCode>. This is the only way to store arbitrary files—repos
            ({" "}<InlineCode>git commit</InlineCode> / <InlineCode>repo commit</InlineCode>) track the corpus and
            verified claims, not files.
          </P>
          <CodeBlock code={`paperclip upload analysis.json report.html --into analyses/my-topic`} />
          <Note>
            Limits per user: 200 MB per file, 2,000 pages per PDF, 10,000 documents, 10 GB total.
          </Note>
        </Section>

        <Section id="clipboard-chrome-extension" title="Clipboard · Chrome extension">
          <P>
            The <strong>Paperclip by GXL</strong> Chrome extension saves papers into your
            Clipboard with one click, straight from your browser—using your own cookies, so
            institutional access to paywalled PDFs works. Get the source from{" "}
            <a
              href="https://github.com/GXL-ai/gxl/tree/main/apps/chrome-extension"
              target="_blank"
              rel="noreferrer"
              className={docTextLinkClass}
            >
              apps/chrome-extension on GitHub
            </a>
            .
          </P>
          <P>
            On known paper sites (Nature, arXiv, PubMed Central, NeurIPS, and other academic
            publishers) a floating <strong>Add to Paperclip</strong> button appears
            bottom-right. One click downloads the PDF and uploads it to your Clipboard, where
            it becomes searchable within seconds. On any other page, the toolbar popup can
            save the page&apos;s PDF—or capture its HTML and parse it into clean text.
          </P>
          <P>
            <strong>Installation.</strong> The extension is not on the Chrome Web Store yet,
            so it installs as an unpacked extension:
          </P>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-decimal">
            <li>
              Clone the repo (or download it as a ZIP) so you have the{" "}
              <InlineCode>apps/chrome-extension</InlineCode> folder locally.
            </li>
            <li>
              Open <InlineCode>chrome://extensions/</InlineCode> in Chrome.
            </li>
            <li>Enable <strong>Developer mode</strong> (toggle in the top right).</li>
            <li>
              Click <strong>Load unpacked</strong> and select the{" "}
              <InlineCode>apps/chrome-extension</InlineCode> folder.
            </li>
            <li>
              (Optional) Enable <strong>&ldquo;Allow access to file URLs&rdquo;</strong> on
              the extension card to save local PDFs opened in the browser.
            </li>
            <li>
              Click the extension icon in your toolbar and press{" "}
              <strong>Log in to Paperclip</strong>. You sign in normally in the web app; the
              extension picks up your session automatically—no API keys needed.
            </li>
          </ul>
          <P>
            The toolbar popup includes a destination-folder field (nested paths such as{" "}
            <InlineCode>reviews/oa</InlineCode> are supported) and remembers the last folder
            you used. The on-page one-click button always saves to{" "}
            <InlineCode>chrome-downloads</InlineCode> for speed.
          </P>
          <Note>
            The extension is currently in internal testing and hard-wired to the staging
            environment (<InlineCode>paperclip.staging.gxl.ai</InlineCode>). It never
            auto-runs on arbitrary sites: the on-page button only appears on known publisher
            domains, and capturing any other page is always an explicit action from the
            toolbar popup.
          </Note>
        </Section>

        <Section id="clipboard-browse" title="Clipboard · Browsing & organizing">
          <P>Browse and read your clipboard like any other paperclip directory:</P>
          <CodeBlock code={`# List folders / documents\npaperclip ls /clipboard/\npaperclip ls /clipboard/my_papers/\n\n# Recursive listing\npaperclip tree /clipboard/\n\n# Read a document\npaperclip head -20 /clipboard/my_papers/usr_c1f9c5eac891/content.lines\npaperclip cat /clipboard/my_papers/usr_c1f9c5eac891/meta.json`} />
          <P>Organize with the usual filesystem verbs (moves and renames are metadata-only):</P>
          <CodeBlock code={`# Create a folder (nesting allowed)\npaperclip mkdir /clipboard/oncology/\n\n# Move or rename a folder or document\npaperclip mv /clipboard/my_papers /clipboard/oncology/\npaperclip mv /clipboard/a/usr_abc /clipboard/a/renamed.pdf\n\n# Storage summary, find files, counts\npaperclip du /clipboard/\npaperclip find "*.pdf" /clipboard/`} />
        </Section>

        <Section id="clipboard-search" title="Clipboard · Searching">
          <P>Search and grep across your uploaded documents:</P>
          <CodeBlock code={`# Keyword search across the whole clipboard\npaperclip search -s clipboard "deep learning"\n\n# Scope the search to one folder\npaperclip search -s clipboard/my-review "dose escalation"\n\n# Full-text regex over clipboard docs\npaperclip grep "methods" /clipboard/\npaperclip grep "results" /clipboard/my_papers/usr_c1f9c5eac891/content.lines\n\n# Query your clipboard AND the public corpus, then merge\npaperclip search -s pmc,clipboard "CRISPR base editing"`} />
          <P>
            Your clipboard is a separate search backend from the public corpus—a single{" "}
            <InlineCode>-s clipboard</InlineCode> searches only your documents. Pass comma-separated
            sources to fan out to both and present the union.
          </P>
          <P>
            Figures and tables in uploaded PDFs are extracted, so you can analyze them with vision:
          </P>
          <CodeBlock code={`paperclip ask-image /clipboard/my_papers/usr_c1f9c5eac891/figures/figure_1.png "What does this figure show?"`} />
        </Section>

        <Section id="clipboard-share" title="Clipboard · Sharing">
          <P>
            Share a folder with a teammate by email. Viewers can read and search the folder; editors can
            also add and remove documents.
          </P>
          <CodeBlock code={`# Share a folder (default role: viewer)\npaperclip share my_papers bob@company.com\n\n# Grant edit access\npaperclip share my_papers bob@company.com --role editor\n\n# Revoke access\npaperclip unshare my_papers bob@company.com`} />
          <P>
            Sharing is scoped to the folder—the rest of your clipboard stays private.
          </P>
        </Section>

        <Section id="clipboard-sync" title="Clipboard · Syncing local folders">
          <P>
            Keep a local folder mirrored to your clipboard. Register it once, then{" "}
            <InlineCode>sync run</InlineCode> uploads new and modified PDFs and removes documents you
            deleted locally.
          </P>
          <CodeBlock code={`# Register a local folder (optionally name the remote prefix)\npaperclip sync add ~/papers --prefix oncology\n\n# Sync: upload new/modified, prune deleted (preview with --dry-run)\npaperclip sync run\npaperclip sync run --dry-run\n\n# One-shot upload without registering\npaperclip sync upload ~/my_papers/\n\n# Status and cleanup\npaperclip sync status\npaperclip sync rm oncology        # or a usr_ id, or --all`} />
        </Section>

        <Section id="clipboard-manage" title="Clipboard · Deleting files">
          <P>Delete documents or folders:</P>
          <CodeBlock code={`# Delete a folder\npaperclip rm /clipboard/my_papers -R\n\n# Delete a single document\npaperclip rm /clipboard/usr_c1f9c5eac891\n\n# Delete everything\npaperclip rm /clipboard -R`} />
          <P>
            Deletes remove documents, extracted text, and search indexes
            from Paperclip&apos;s live systems. They cannot be recovered
            through Paperclip. Encrypted provider backups may persist
            temporarily under their retention policies.
          </P>
        </Section>

        <Section id="clipboard-limits" title="Clipboard · Limits">
          <P>Current limits per user:</P>
          <table className="w-full border-t border-zinc-200 text-sm">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400">Limit</th>
                <th className="px-3 py-2 text-left font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/70 border-t border-zinc-200/70">
              <tr><td className="px-3 py-2">Max file size</td><td className="px-3 py-2">200 MB</td></tr>
              <tr><td className="px-3 py-2">Max pages per PDF</td><td className="px-3 py-2">2,000</td></tr>
              <tr><td className="px-3 py-2">Max documents</td><td className="px-3 py-2">10,000</td></tr>
              <tr><td className="px-3 py-2">Max storage</td><td className="px-3 py-2">10 GB</td></tr>
              <tr><td className="px-3 py-2">Formats</td><td className="px-3 py-2">PDF (parsed); JSON/HTML/CSV/MD/PDF artifacts via <InlineCode>upload</InlineCode></td></tr>
              <tr><td className="px-3 py-2">Parse time</td><td className="px-3 py-2">~10–20s per document</td></tr>
              <tr><td className="px-3 py-2">Parallel uploads</td><td className="px-3 py-2">Up to 4 concurrent</td></tr>
            </tbody>
          </table>
        </Section>

        {/* ── Python SDK ───────────────────────────────────────── */}
        <Section id="sdk-overview" title="Python SDK · Overview">
          <P>
            The <InlineCode>gxl-paperclip</InlineCode> package ships a Python SDK alongside
            the CLI. Installing it gives you both the <InlineCode>paperclip</InlineCode>{" "}
            command and the <InlineCode>gxl_paperclip</InlineCode> module.
          </P>
          <P>
            Use the SDK when you want typed Python APIs, structured return values, and
            programmatic control flow (scripts, tests, notebooks, backend jobs). Use the CLI
            when you want interactive exploration, shell composition, or the same invocations
            you would run in a terminal. Neither is “for humans” or “for agents” exclusively —
            choose by task.
          </P>
          <P>
            The SDK is a thin, typed wrapper around the same endpoints the CLI uses. Typed
            methods take Pythonic keyword arguments and return structured result objects
            instead of printing raw strings.
          </P>
        </Section>

        <Section id="sdk-install" title="Install & authenticate">
          <P>
            The SDK authenticates with <strong>API keys</strong>. Mint one from the
            dashboard and expose it as an env var:
          </P>
          <CodeBlock
            code={`export PAPERCLIP_API_KEY="pk_..."`}
            language="bash"
          />
          <CodeBlock
            code={`from gxl_paperclip import PaperclipClient, APIKeyAuth

client = PaperclipClient.from_env()              # reads PAPERCLIP_API_KEY
# — or pass an explicit strategy —
client = PaperclipClient(auth=APIKeyAuth("pk_..."))`}
            language="python"
          />
          <Note>
            <InlineCode>PaperclipClient.from_env()</InlineCode> falls back to the credentials
            saved by <InlineCode>paperclip login</InlineCode> (
            <InlineCode>~/.paperclip/credentials.json</InlineCode>) when no API key env var is
            set — handy on a workstation where you've already signed in. OAuth is
            CLI-focused; programmatic users should use an API key.
          </Note>
        </Section>

        <Section id="sdk-quickstart" title="Python SDK · Quick start">
          <CodeBlock
            code={`from gxl_paperclip import PaperclipClient

client = PaperclipClient.from_env()

result = client.search("CRISPR lipid nanoparticle", limit=5, source="pmc")
print(result.output)        # same formatted text the CLI prints
print(result.result_id)     # e.g. "s_14bebc10" — pass to map_()

for event in client.map_("What delivery methods were used?", from_results=result.result_id):
    if event.type == "progress":
        print(f"{event.completed}/{event.total} papers done")
    else:
        print(event.output)`}
            language="python"
          />
          <Note>
            Every optional kwarg defaults to <InlineCode>None</InlineCode> (or{" "}
            <InlineCode>False</InlineCode> for boolean flags). Leaving it unset means the
            flag is omitted and the <em>server-side default</em> applies. The tables below
            list each server-side default.
          </Note>
        </Section>

        <Section id="sdk-search" title="client.search()">
          <CodeBlock
            code={`client.search(
    query: str,
    *,
    limit: int | None = None,
    source: str | None = None,
    exact: bool = False,
    since: str | None = None,
    sort: str | None = None,
    author: str | None = None,
    journal: str | None = None,
    year: int | str | None = None,
    type: str | None = None,
    category: str | None = None,
    mode: str | None = None,
    all: bool = False,
    timeout: float | None = None,
) -> ExecuteResult`}
            language="python"
          />
          <ParamTable
            params={[
              { name: "query", type: "str", required: true, description: "Natural-language search query." },
              { name: "limit", type: "int", default: "100 (max 1000)", description: "Max results returned by the server." },
              { name: "source", type: "str", default: "all sources", description: 'Filter by source: "pmc", "biorxiv", "medrxiv", "arxiv", "fda", "trials", "patents", or a comma-separated list.' },
              { name: "exact", type: "bool", default: "False", description: "When True, the server switches search mode to phrase matching." },
              { name: "since", type: "str", default: "no recency filter", description: 'Recency window like "7d", "30d", "6m", "1y".' },
              { name: "sort", type: "str", default: '"relevance"', description: 'Pass "date" for newest-first ordering.' },
              { name: "author", type: "str", default: "no filter", description: "Author name substring match." },
              { name: "journal", type: "str", default: "no filter", description: "Journal name (PMC only)." },
              { name: "year", type: "int | str", default: "no filter", description: 'Publication year, e.g. 2024.' },
              { name: "type", type: "str", default: "no filter", description: 'PMC article type, e.g. "review-article".' },
              { name: "category", type: "str", default: "no filter", description: 'bioRxiv category, e.g. "Neuroscience".' },
              { name: "mode", type: "str", default: '"any"', description: 'Term match mode: "any", "all", "50%", "75%".' },
              { name: "all", type: "bool", default: "False", description: "When True, searches the full corpus instead of the recency-weighted slice." },
              { name: "timeout", type: "float", default: "120 s", description: "Client timeout in seconds." },
            ]}
          />
        </Section>

        <Section id="sdk-lookup" title="client.lookup()">
          <CodeBlock
            code={`client.lookup(
    field: str,
    value: str,
    *,
    limit: int | None = None,
    timeout: float | None = None,
) -> ExecuteResult`}
            language="python"
          />
          <P>
            Look up papers by a metadata field — <InlineCode>doi</InlineCode>,{" "}
            <InlineCode>pmc</InlineCode>, <InlineCode>pmid</InlineCode>,{" "}
            <InlineCode>author</InlineCode>, <InlineCode>title</InlineCode>,{" "}
            <InlineCode>journal</InlineCode>, <InlineCode>year</InlineCode>,{" "}
            <InlineCode>keywords</InlineCode>, and more. Match is partial and
            case-insensitive.
          </P>
          <ParamTable
            params={[
              { name: "field", type: "str", required: true, description: "Metadata field to search." },
              { name: "value", type: "str", required: true, description: "Value to match." },
              { name: "limit", type: "int", default: "25", description: "Max results." },
              { name: "timeout", type: "float", default: "120 s", description: "Client timeout in seconds." },
            ]}
          />
        </Section>

        <Section id="sdk-sql" title="client.sql()">
          <CodeBlock
            code={`client.sql(
    query: str,
    *,
    source: str | None = None,
    timeout: float | None = None,
) -> ExecuteResult`}
            language="python"
          />
          <P>
            Read-only SQL against the <InlineCode>documents</InlineCode> table.
            Server-side: 15s query timeout, 200-row cap, <InlineCode>SELECT</InlineCode> only.
          </P>
          <ParamTable
            params={[
              { name: "query", type: "str", required: true, description: "SELECT statement on the documents table." },
              { name: "source", type: "str", default: '"all"', description: 'Pass "pmc" or "biorxiv" to restrict the query to one source.' },
              { name: "timeout", type: "float", default: "120 s", description: "Client timeout in seconds." },
            ]}
          />
        </Section>

        <Section id="sdk-map" title="client.map_()">
          <CodeBlock
            code={`client.map_(
    question: str,
    *,
    from_results: str,
    timeout: float | None = None,
) -> Iterator[MapEvent]`}
            language="python"
          />
          <P>
            Run an AI reader across every paper in a prior search/lookup result set. Yields{" "}
            <InlineCode>MapProgressEvent</InlineCode> updates during the run (OAuth streaming
            path) followed by a single <InlineCode>MapResultEvent</InlineCode>.
          </P>
          <ParamTable
            params={[
              { name: "question", type: "str", required: true, description: "Prompt asked against each paper." },
              { name: "from_results", type: "str", required: true, description: 'Result ID from a prior search/lookup, e.g. "s_14bebc10".' },
              { name: "timeout", type: "float", default: "300 s", description: "Map uses the slow-command default." },
            ]}
          />
        </Section>

        <Section id="sdk-pull" title="client.pull()">
          <CodeBlock
            code={`client.pull(
    target: str,
    dest: str | None = None,
    *,
    timeout: float | None = None,
) -> ExecuteResult`}
            language="python"
          />
          <P>
            Download a paper or single file from the virtual filesystem. The{" "}
            <InlineCode>ExecuteResult</InlineCode> contains{" "}
            <InlineCode>download_url</InlineCode> /{" "}
            <InlineCode>download_filename</InlineCode> for binary payloads.
          </P>
          <ParamTable
            params={[
              { name: "target", type: "str", required: true, description: 'Paper or file, e.g. "PMC10791696" or "PMC10791696/figures/fig1.jpg".' },
              { name: "dest", type: "str", default: "current directory", description: "Destination directory on the server side of the command." },
              { name: "timeout", type: "float", default: "120 s", description: "Client timeout in seconds." },
            ]}
          />
        </Section>

        <Section id="sdk-ask-image" title="client.ask_image()">
          <CodeBlock
            code={`client.ask_image(
    path: str,
    question: str | None = None,
    *,
    fn: str | None = None,
    timeout: float | None = None,
) -> ExecuteResult`}
            language="python"
          />
          <ParamTable
            params={[
              { name: "path", type: "str", required: true, description: 'Figure path, e.g. "PMC11576387/figures/fx1.jpg".' },
              { name: "question", type: "str", default: '"Describe this figure in detail."', description: "Free-form question about the figure." },
              { name: "fn", type: "str", default: "free-form prompt", description: 'Canned flows: "describe" or "extract-data".' },
              { name: "timeout", type: "float", default: "300 s", description: "ask-image uses the slow-command default." },
            ]}
          />
        </Section>

        <Section id="sdk-bash" title="client.bash()">
          <CodeBlock
            code={`client.bash(
    script: str,
    *,
    timeout: float | None = None,
) -> ExecuteResult`}
            language="python"
          />
          <P>
            Run an arbitrary server-side pipeline, exactly like{" "}
            <InlineCode>paperclip bash '…'</InlineCode>.
          </P>
          <CodeBlock
            code={`result = client.bash('search "protein folding" | grep -i "deep learning"')`}
            language="python"
          />
          <ParamTable
            params={[
              { name: "script", type: "str", required: true, description: "A single shell-style command string." },
              { name: "timeout", type: "float", default: "120 s", description: "Client timeout in seconds." },
            ]}
          />
        </Section>

        <Section id="sdk-results" title="client.results">
          <CodeBlock
            code={`client.results.list(*, limit: int | None = None) -> list[ResultRow]
client.results.get(result_id: str) -> ResultData`}
            language="python"
          />
          <P>
            <InlineCode>results.list()</InlineCode> returns recently saved results for the
            authenticated user. <InlineCode>results.get()</InlineCode> fetches the raw saved
            output for a specific ID (e.g. <InlineCode>"s_14bebc10"</InlineCode>,{" "}
            <InlineCode>"m_ec2c9cc9"</InlineCode>).
          </P>
          <ParamTable
            params={[
              { name: "limit", type: "int", default: "20 (server default)", description: "Max rows returned by results.list()." },
              { name: "result_id", type: "str", required: true, description: "Result ID passed to results.get()." },
            ]}
          />
        </Section>

        <Section id="sdk-papers" title="client.papers.*">
          <P>
            Typed wrappers over the virtual-filesystem commands. Each method returns an{" "}
            <InlineCode>ExecuteResult</InlineCode>.
          </P>
          <CodeBlock
            code={`client.papers.cat(path)
client.papers.head(path, *, lines=None)      # default 10 lines (server default)
client.papers.tail(path, *, lines=None)      # default 10 lines (server default)
client.papers.ls(path)
client.papers.grep(pattern, path, *, ignore_case=False, extended=False)
client.papers.scan(path, patterns)           # list of patterns OR'd together`}
            language="python"
          />
          <Note>
            When a flag like <InlineCode>lines</InlineCode>,{" "}
            <InlineCode>ignore_case</InlineCode>, or <InlineCode>extended</InlineCode> is left
            unset, the SDK omits it from the command and the server's shell-style default
            applies.
          </Note>
        </Section>

        <Section id="sdk-execute" title="Escape hatches: execute() & stream()">
          <CodeBlock
            code={`client.execute(command: str, args: Sequence[str] | None = None, *, timeout=None) -> ExecuteResult
client.stream(command: str, args: Sequence[str] | None = None, *, timeout=None) -> Iterator[MapEvent]`}
            language="python"
          />
          <P>
            For commands without a typed wrapper — <InlineCode>sed</InlineCode>,{" "}
            <InlineCode>awk</InlineCode>, <InlineCode>sort</InlineCode>,{" "}
            <InlineCode>cut</InlineCode>, <InlineCode>tr</InlineCode>,{" "}
            <InlineCode>jq</InlineCode>, or any future server command — pass the argv tokens
            as a list and the SDK shell-quotes them for you.
          </P>
          <CodeBlock
            code={`result = client.execute(
    "awk",
    ["-F", "\\t", "{print $1}", "/papers/PMC1/content.lines"],
)`}
            language="python"
          />
          <P>
            <InlineCode>stream()</InlineCode> is a streaming equivalent; today only{" "}
            <InlineCode>"map"</InlineCode> streams (other commands raise{" "}
            <InlineCode>ValueError</InlineCode>).
          </P>
        </Section>

        <Section id="sdk-errors" title="Errors & result types">
          <P>All HTTP and network failures raise a subclass of <InlineCode>PaperclipError</InlineCode>:</P>
          <CodeBlock
            code={`from gxl_paperclip import (
    AuthError, RateLimitError, NotFoundError, ServerError,
    RequestTimeoutError, NetworkError,
)

try:
    client.search("AlphaFold")
except AuthError:
    ...  # invalid API key or expired credentials
except RateLimitError:
    ...  # HTTP 429
except RequestTimeoutError:
    ...  # client-side timeout
except ServerError as exc:
    print(exc.status_code, exc.body)`}
            language="python"
          />
          <P>Result dataclasses you'll encounter:</P>
          <ul className="text-base text-zinc-500 leading-[1.75] mb-4 space-y-1 pl-5 list-disc">
            <li><InlineCode>ExecuteResult</InlineCode> — output, exit_code, elapsed_ms, result_id, download_url, download_filename, cwd, raw</li>
            <li><InlineCode>MapProgressEvent</InlineCode> — total, completed, failed, elapsed_s</li>
            <li><InlineCode>MapResultEvent</InlineCode> — output, result_id, elapsed_ms, exit_code</li>
            <li><InlineCode>ResultRow</InlineCode> — result_id, command, raw_input, latency_ms, created_at, raw</li>
            <li><InlineCode>ResultData</InlineCode> — result_id, output, command, raw_input, latency_ms, created_at, raw</li>
            <li><InlineCode>HealthStatus</InlineCode> — reachable, output, exit_code, elapsed_ms</li>
          </ul>
        </Section>

        {/* Footer */}
        <div className="pt-8 pb-12 text-center border-t border-zinc-100 mt-10">
          <a
            href="https://gxl.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Built by GXL <ExternalLink className="w-3 h-3" />
          </a>
        </div>

      </div>
    </div>
  );
};
